import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleCheck, X } from 'lucide-react';
import maplibregl from 'maplibre-gl';
// AK-388: V2 использует нативный GeolocateControl (мобила) → нужен базовый
// стиль контролов MapLibre (V2 иначе его не грузит — RightRail кастомный) +
// Earth Tech override (scoped под контейнер V2-карты).
import 'maplibre-gl/dist/maplibre-gl.css';
import '@/components/map/geolocateControl.css';
import { AppShell } from '@/components/layout/AppShell';
import { LayerManager } from '@/lib/map/LayerManager';
import { LAYER_REGISTRY } from '@/lib/map/layers/registry';
import { useMapStore } from '@/lib/map/useMapStoreV2';
import { useDatesEffect } from '@/lib/map/effects/datesEffect';
import { useCoverageEffect } from '@/lib/map/effects/coverageEffect';
import { useLayerSyncEffect } from '@/lib/map/effects/layerSyncEffect';
import { useRasterSourceEffect } from '@/lib/map/effects/rasterSourceEffect';
import { usePrefetchDatesEffect } from '@/lib/map/effects/prefetchDatesEffect';
import { useCropPatternAtlasEffect } from '@/lib/map/effects/cropPatternAtlasEffect';
import { usePillSpriteEffect } from '@/lib/map/effects/pillSpriteEffect';
import { useGeolocateControl } from '@/lib/map/effects/geolocateControlEffect';
import { useRasterTilesLoading } from '@/lib/map/useRasterTilesLoading';
import { recreateMvtSource } from '@/lib/map/recreateMvtSource';
import { requestScopeGeomVersionRefresh } from '@/lib/map/scopeGeomVersion';
import { installMapResizeGuard } from '@/lib/map/installMapResizeGuard';
import { scopeFromUrl } from '@/lib/map/scopeFromUrl';
import { resolveInitialScopeWithFarmFallback, type ScopeRef } from '@/lib/map/pickInitialScope';
import type { MapState, ActiveScope, Basemap } from '@/lib/map/state/mapState';
import { RightRail } from '@/components/map/RightRail';
import { SatelliteStrip } from '@/components/map/SatelliteStrip';
import { RasterLoadingOverlay } from '@/components/map/RasterLoadingOverlay';
import { MapVerticalLegend } from '@/components/map/MapVerticalLegend';
// AK-320 «Линейка на карте» — measure-инструмент (отдельный стор + контроллер).
import { MeasurePanel } from '@/components/map/MeasurePanel';
import { MeasureController } from '@/lib/map/measure/MeasureController';
import { useMeasureStore } from '@/lib/map/measure/measureStore';
import { applyMeasureState, initMeasurePrev } from '@/lib/map/measure/applyMeasureState';
// AK-321 «Редактор контура — обвести поле» — terra-draw select-mode редактор
// (отдельный стор + контроллер, изолированный namespace `td-edit-`, ADR-038).
import { ContourEditorPanel } from '@/components/map/ContourEditorPanel';
import {
  ContourEditorController,
  type EditorGeometry,
} from '@/lib/map/editor/ContourEditorController';
import { useEditorStore } from '@/lib/map/editor/editorStore';
// AK-334/335 «Tap-to-field» — режим «+ Поле»: тап → детект границ → черновик →
// форма сохранения. Отдельный стор + контроллер (ADR-038 изоляция, namespace af-).
import { AddFieldPanel } from '@/components/map/AddFieldPanel';
import {
  FirstFieldDoneCard,
  shouldShowFirstFieldDone,
} from '@/components/map/FirstFieldDoneCard';
import { AddFieldController } from '@/lib/map/addField/AddFieldController';
import { useAddFieldStore } from '@/lib/map/addField/addFieldStore';
import { decideReturnFromEditor } from '@/lib/map/addField/returnFromEditor';
import {
  applyAddFieldState,
  initAddFieldPrev,
} from '@/lib/map/addField/applyAddFieldState';
import { delineateField, DelineateError } from '@/lib/map/addField/delineateApi';
import { useAutodetectBasemapSync } from '@/lib/map/addField/useAutodetectBasemapSync';
import { polygonAreaSqm } from '@/lib/map/measure/geo';
import type { LngLat } from '@/lib/map/editor/fieldGeojson';
import { getCachedFieldGeometry, loadFieldGeometry } from '@/lib/map/editor/fieldGeojson';
// AK-368c «Импорт полей из файла» — превью пачки контуров из KML/KMZ/SHP на
// карте (отдельный стор + контроллер, namespace af-import-, ADR-038 изоляция).
import { ImportFilePanel } from '@/components/map/ImportFilePanel';
import { ImportController } from '@/lib/map/import/ImportController';
import { useImportStore } from '@/lib/map/import/importStore';
import { applyImportState, initImportPrev } from '@/lib/map/import/applyImportState';
import {
  saveImport,
  listBrigades,
  createBrigade,
  rollbackImportBatch,
  type SaveFeaturePayload,
  type Brigade,
} from '@/lib/map/import/importApi';
import {
  firstIntersectingField,
  type ExistingFieldRings,
  type LngLatPt,
} from '@/lib/map/import/intersectFields';
import { nextFieldName } from '@/lib/map/addField/autoName';
// AK-149: map chips (company / cluster / farm HTML markers).
// Hook ported earlier (frontend-vite/src/components/map/useFarmChips.ts);
// styles ported here in this PR (src/styles/chips.css → globals.css).
import { useFarmChips } from '@/components/map/useFarmChips';
import {
  getMapInit,
  invalidateMapInit,
  type MapInitData,
} from '@/lib/map/mapInitCache';
import { userLevelFromMap, mlForUserLevel } from '@/lib/map/zoomLevels';
import { dispatchScopeChanged } from '@/lib/scopeEvent';
// 2026-05-08: TopBarPill + ScopeDrawer imports removed — components were
// duplicate scope selector to the AppShell one (then the header pill, since
// AK-895 — the «Хозяйство ▾» switcher atop the sidebar, `FarmSwitcher`).
// Founder confirmed pill-on-map was never intended — the map-area "pills"
// in Section 4 spec were centroid labels (separate feature, not yet
// implemented). AK-103 mount on 2026-05-08 morning was a regression and
// has been reverted.
import { buildFieldPopoverHtml, type FieldPopoverProps } from '@/lib/map/fieldPopover';
// «Посев с карты» (запрос founder 2026-07-27): живой блок посева внутри
// HTML-попапа поля — статус по сезону/зонам + кнопка «Отметить: посеяно».
import { FieldSowBlock } from '@/components/map/FieldSowBlock';
// AK-772 («выверка полей с карты», решение founder 28.08): действия над полем
// прямо в попапе — переименовать / архивировать / уйти в редактор геометрии.
// Диалоги живут на уровне страницы (см. ниже), а не в портале: попап может
// закрыться и унести портал, а диалог посреди ввода причины исчезать не должен.
import { FieldActionsBlock } from '@/components/map/FieldActionsBlock';
import { FieldsArchiveDialog } from '@/components/fields/FieldsArchiveDialog';
import { RenameFieldDialog } from '@/pages/structure/components/RenameFieldDialog';
import { archiveSingleField } from '@/pages/fields/archiveBatch';
import { MAP_MV_LAG_MS } from '@/lib/map/mapViewLag';
import { toast } from '@/lib/toast';
import { useActiveSeason } from '@/lib/season/SeasonContext';
import { openNavigator, openRoute } from '@/lib/geo/navigateLinks';
import { productOf } from '@/lib/map/derived/productOf';
import { useScopeChanged } from '@/lib/scopeEvent';
import { api } from '@/lib/api';
import { useCan, PERM } from '@/lib/auth/usePermissions';
import { captureException } from '@/lib/sentry';
import { rasterTileSize } from '@/lib/featureFlags';
import {
  IMAGERY_TILE_URL,
  TRANSPORT_TILE_URL,
  PLACES_TILE_URL,
  OSM_STREETS_PATH,
} from '@/lib/map/basemapUrls';
import { fetchScopeExtents } from '@/lib/http/sharedQueries';
import { loadMapPrefs, persistMapPrefs } from '@/lib/map/mapPrefsIo';

// AK-193: NDVI/RGB/cloud rasters only exist for z9-16 on the backend (the COG
// pyramid + scope-mask render path). `minzoom:9` stops MapLibre from issuing
// guaranteed-empty requests below z9; `maxzoom:16` makes it OVERZOOM (stretch
// the z16 tile) above z16 instead of fetching native high-zoom tiles the
// backend would render on the fly. Legacy MapCanvas registered these bounds;
// the V2 rewrite dropped them (audit Q3/F3) → extra fetches + blank tiles.
const RASTER_MINZOOM = 9;
const RASTER_MAXZOOM = 16;

const MVT_PATH = '/api/v1/tiles/fields/{z}/{x}/{y}.mvt';
/**
 * AK-516: версия схемы MVT — зеркало заголовка `X-Tile-Layout` бэкенда.
 * Уходит в URL тайла (`_s`) и служит cache-key'ом на схему: бампаешь на бэке —
 * бампаешь здесь, и все клиенты сразу получают тайлы новой схемы, не дожидаясь
 * истечения 24-часового `Cache-Control`.
 */
const MVT_SCHEMA_VERSION = 'styled-v3';
// Bug #3 (Sprint 2026-19): basemap-source URL — реактивно swap'ается
// при `state.basemap` change через `setSourceTiles()`. Раньше map
// инициализировался с одним статичным Esri URL и переключатель
// ничего не делал.
// Перф-аудит 14.08, корень 1: URL'ы подложки живут в `@/lib/map/basemapUrls`
// и указывают на НАШ nginx-прокси, а не на server.arcgisonline.com напрямую.
const basemapUrls = (): Record<Basemap, string> => ({
  satellite: IMAGERY_TILE_URL(),
  // Bug C (Sprint 2026-19 Day 2): hybrid = satellite (z=1 basemap) + Esri
  // Reference World_Transportation overlay (z=1.5 basemap-transport). Сам
  // `basemap` source при hybrid'е остаётся imagery URL'ом, transport
  // overlay рисуется поверх через `basemap-transport` layer/source с
  // visibility predicate s.basemap === 'hybrid'.
  hybrid: IMAGERY_TILE_URL(),
  streets: OSM_STREETS_PATH,
});
// Transparent 1×1 PNG used as a no-op tile when a raster source is off.
// MapLibre requires at least one valid tile URL to keep the source healthy.
const EMPTY_TILE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=';

/**
 * EPSG:3857 → EPSG:4326 (degree). Mirrors `ScopeDrawer.tsx::mercatorToLngLat`
 * (deleted in Sprint 2026-21 V2 cleanup). Pulled inline here so that
 * AK-102 fitBounds-on-scope-change does не require resurrecting the drawer.
 */
function mercatorToLngLat([x, y]: [number, number]): [number, number] {
  const lng = (x / 20037508.34) * 180;
  let lat = (y / 20037508.34) * 180;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180)) - Math.PI / 2);
  return [lng, lat];
}

/**
 * Run `fn` as soon as the map is ready to drive an eased camera move, firing
 * on the FIRST of: already-loaded (sync), `idle`, `load`, or a short timeout.
 *
 * AK-193 (#331 fitBounds regression): #331 deferred fitBounds to
 * `map.once('load')` only. When `load` had already fired (a scope change after
 * the initial map load — the common path) OR when MapLibre's `isStyleLoaded()`
 * lies and stays false, the deferred call was dropped and the map never zoomed
 * to the selected scope. This helper guarantees the fit runs exactly once on
 * whichever signal arrives first:
 *   - `map.loaded()` true now           → run synchronously
 *   - `idle`                            → style + tiles settled, safe to ease
 *   - `load`                            → style ready (back-compat with #331)
 *   - timeout fallback (700ms)          → run regardless if no event fired
 * `essential: true` on the fitBounds itself keeps the move alive under
 * prefers-reduced-motion.
 */
function runFitWhenReady(map: maplibregl.Map, fn: () => void): void {
  if (map.loaded()) {
    fn();
    return;
  }
  let done = false;
  const fire = () => {
    if (done) return;
    done = true;
    window.clearTimeout(timer);
    map.off('idle', fire);
    map.off('load', fire);
    fn();
  };
  // `idle` fires after the first frame is composited and tiles settle — the
  // most reliable "camera can move now" signal, and unlike `once('load')` it
  // keeps firing so a post-load subscription still catches it.
  map.on('idle', fire);
  map.on('load', fire);
  // Belt-and-suspenders: if neither event ever fires (the isStyleLoaded quirk),
  // run anyway so a scope selection always zooms.
  const timer = window.setTimeout(fire, 700);
}

/**
 * AK-102 (founder UAT 2026-05-08): when activeScope changes (via TopBar
 * select OR ScopeDrawer click), fetch /scope/extents and animate map.fitBounds
 * to the bbox. Returns silently on company scope (uses '-' as scope_id),
 * malformed responses, or null map (initial render).
 *
 * The endpoint returns bbox in EPSG:3857 (per scope_extents MV definition);
 * we convert to lng/lat for MapLibre's `fitBounds`.
 */
/**
 * Что сказать человеку, когда «Готово» не смогло замкнуть контур.
 *
 * Разные причины — разные советы: тому, кто не начинал рисовать, бесполезно
 * говорить «поставьте третью точку». Редактор при любом исходе остаётся
 * открытым, рисунок — на экране.
 */
const FINISH_DRAW_MESSAGE: Record<
  Exclude<import('@/lib/map/editor/ContourEditorController').FinishDrawOutcome, 'closed'>,
  string
> = {
  'not-drawing':
    'Сначала нарисуйте контур: выберите инструмент или кликайте по углам поля.',
  'too-few-points':
    'Поставьте минимум 3 точки по углам поля — тогда контур замкнётся сам.',
  failed:
    'Не получилось замкнуть контур автоматически. Замкните его сами, кликнув по первой точке.',
  unavailable: 'Редактор ещё не готов — попробуйте через мгновение.',
};

async function fitBoundsToScope(
  map: maplibregl.Map | null,
  scope: ActiveScope,
): Promise<void> {
  if (!map) return;
  try {
    // Перф-аудит 14.08 (корень 6): общий кэш с RightRail + negative-cache на
    // 404 («в холдинге нет полей» — штатный ответ, а не ошибка).
    const body = await fetchScopeExtents(scope.type, scope.id ?? null);
    if (!body?.bbox || body.bbox.length !== 4) return;
    const [minLng, minLat] = mercatorToLngLat([body.bbox[0], body.bbox[1]]);
    const [maxLng, maxLat] = mercatorToLngLat([body.bbox[2], body.bbox[3]]);
    // AK-297 (design review 2026-06-27): a tight bbox (small farm / single
    // sub-array / one brigade) made fitBounds settle at z17-19, where the Esri
    // satellite imagery is already overzoomed into a blurry mush. Cap the fit
    // depth for the granular scopes to a still-readable field-level zoom (z15.5)
    // so the camera lands where individual fields are legible AND the imagery is
    // still sharp. Holding/company stay uncapped — they NEED to show the whole
    // (often vast) extent and never over-zoom anyway.
    const FIT_MAX_ZOOM_BY_SCOPE: Partial<Record<ActiveScope['type'], number>> = {
      farm: 15.5,
      cluster: 15.5,
      brigade: 15.5,
      array: 16,
      field: 16,
    };
    const fitMaxZoom = FIT_MAX_ZOOM_BY_SCOPE[scope.type];
    const runFit = () => {
      try {
        map.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          // essential: true so the eased camera move is never short-circuited
          // by prefers-reduced-motion (a short-circuited animation could leave
          // isMoving() stuck without ever firing moveend).
          // maxZoom (AK-297): only for granular scopes — keeps farm/sub fits at
          // a sharp, field-level depth instead of blurry overzoom.
          {
            padding: 80,
            duration: 600,
            essential: true,
            ...(fitMaxZoom !== undefined ? { maxZoom: fitMaxZoom } : {}),
          },
        );
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[mapV2] fitBounds failed', err);
      }
    };
    // WHITE-CANVAS FIX (V2 first-load): never start the eased camera animation
    // before the style is loaded. On cold load `tryFit` resolves as soon as the
    // map *instance* exists (mapRef set synchronously, before `load`). A
    // fitBounds(duration>0) started pre-`load` runs against a 0/stale-sized GL
    // canvas whose render loop hasn't composited a frame yet → the animation
    // never settles, isMoving() stays stuck true and isStyleLoaded() stays
    // false (the founder-observed white canvas). Deferring guarantees a live
    // render loop is driving the transition so moveend → idle fire.
    //
    // AK-193 FOLLOW-UP (#331 regression, founder UAT 2026-06-25): #331 deferred
    // ONLY to `map.once('load', runFit)`. Two failure modes left scope-select
    // without any zoom-to-scope (map stayed at the wide default view):
    //   1. `load` already fired before fitBoundsToScope was called (the common
    //      case — a scope-changed event arrives long after the initial load).
    //      `once('load')` never fires again → the deferred fit is dropped.
    //   2. The known MapLibre quirk where `isStyleLoaded()` stays false even
    //      after the style is usable → we take the defer branch, but if (1)
    //      also holds the callback is lost.
    // Fix: fire on whichever readiness signal comes first — synchronous if the
    // style is already loaded, else the FIRST of `idle` / `load` / a short
    // timeout fallback. The timeout is the belt-and-suspenders guarantee that
    // a scope selection ALWAYS zooms even if no map event ever fires.
    runFitWhenReady(map, runFit);
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[mapV2] fitBoundsToScope failed', err);
  }
}

/**
 * Bug #2 (Sprint 2026-19, frontend part): build MVT tile URL with scope
 * query params (`farm_id` / `cluster_id`) — backend filter (Bugs 2+4
 * backend) требует их для cluster/farm scope. Для company scope params
 * нет, backend возвращает все поля компании.
 */
/**
 * AK-485 (аудит crop-status-truth §3 R5b / наряд map-popup-vs-label-bug Д1):
 * `season_id` — линза активного сезона. Раньше URL строился ТОЛЬКО из
 * scope, свитчер сезона в шапке на карту не влиял вовсе: подпись/заливка
 * оставались «текущими», а попап того же поля (FieldSowBlock, читает
 * occupancy по `SeasonContext`) уже показывал выбранный сезон — два разных
 * сезона в одном попапе. SQL-выборка тайла пока не параметризована по
 * сезону (см. backend/tiles.py) — параметр меняет URL → инвалидирует
 * MapLibre tile-кэш при смене сезона (тот же принцип, что farm_id/cluster_id).
 */
function buildMvtUrl(scope: ActiveScope, seasonId?: string | null): string {
  const base = `${window.location.origin}${MVT_PATH}`;
  const params = new URLSearchParams();
  if (scope.id) {
    if (scope.type === 'farm') params.set('farm_id', scope.id);
    else if (scope.type === 'cluster') params.set('cluster_id', scope.id);
  }
  if (seasonId) params.set('season_id', seasonId);
  // AK-516: версия СХЕМЫ тайла в ключе кэша. Тайлы отдаются с
  // `Cache-Control: max-age=86400` (+ nginx edge), а `recreateMvtSource`
  // выселяет только in-memory кэш MapLibre — HTTP-кэш на тот же URL вернул бы
  // тайл прежней схемы, и слой зон не появился бы до истечения суток.
  // Константа (а не `Date.now()`) — бампается вместе с `X-Tile-Layout` на
  // бэке и не убивает кэширование в обычной работе.
  params.set('_s', MVT_SCHEMA_VERSION);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Cross-device server-side persist of PersistentMapState subset.
 *
 * Sprint 2026-21 T13: V2 store handles same-device persistence via
 * zustand `persist` middleware → localStorage (PERSISTENT_KEYS).
 * Server-side `/api/v1/users/me/map-preferences` is the cross-device
 * hydration channel — kept as a debounced fire-and-forget POST keyed
 * off MapState changes, not part of the reducer surface.
 *
 * `toWire` includes session vars (activeRasterMode/selectedDate/...) by
 * historical contract; backend stores whatever client sends. We do NOT
 * rehydrate session vars on load (V2 lock-in #1) — they reset per
 * page-load by design.
 *
 * AK-629: сам ввод-вывод живёт в `lib/map/mapPrefsIo.ts` — там же защёлка
 * «PATCH вернул 404 (нет строки членства) → больше не пишем» и гарантия, что
 * чтение настроек никогда не reject'ит и потому не может утащить за собой
 * выбор стартового скоупа в hydrate ниже.
 */

/**
 * Generic tile URL swap. `setTiles()` exists на обоих RasterTileSource
 * и VectorTileSource — переиспользуем для basemap (raster) и fields-mvt
 * (vector). Type-narrow через runtime check.
 *
 * Sprint 2026-21 T13: NDVI/RGB/cloud raster source mutations moved into
 * `useRasterSourceEffect` (T8) — this helper остался для basemap +
 * fields-mvt URL swaps which are scope-driven (not raster-product driven).
 */
function setSourceTiles(map: maplibregl.Map, sourceId: string, url: string) {
  const src = map.getSource(sourceId) as
    | (maplibregl.RasterTileSource & { setTiles?: (u: string[]) => void })
    | (maplibregl.VectorTileSource & { setTiles?: (u: string[]) => void })
    | undefined;
  if (src && typeof src.setTiles === 'function') {
    src.setTiles([url]);
  }
}

/**
 * AK-368c: внешнее кольцо [lng,lat][] из импорт-геометрии Polygon/MultiPolygon
 * для загрузки в редактор контура («Уточнить контур»). Берём первое (крупнейшую
 * часть не выделяем — превью-контуры обычно одночастные). null, если геометрии
 * нет или кольцо вырождено.
 */
function importRingOf(
  geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: number[][][] | number[][][][] } | null,
): LngLat[] | null {
  if (!geometry) return null;
  let ring: number[][] | undefined;
  if (geometry.type === 'Polygon') {
    ring = (geometry.coordinates as number[][][])[0];
  } else {
    ring = (geometry.coordinates as number[][][][])[0]?.[0];
  }
  if (!ring || ring.length < 4) return null;
  return ring as LngLat[];
}

/**
 * AK-371: площадь в га по Polygon | MultiPolygon (клиентское preview; source of
 * truth — сервер, ADR R3). Сумма внешних колец частей минус дырки.
 */
function geometryAreaHa(geometry: EditorGeometry): number {
  const parts = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  let sqm = 0;
  for (const part of parts) {
    for (let i = 0; i < part.length; i++) {
      const ring = part[i];
      if (!ring || ring.length < 4) continue;
      const a = polygonAreaSqm(ring);
      sqm += i === 0 ? a : -a; // внешнее (+), дырки (−)
    }
  }
  return Math.max(0, sqm) / 10_000;
}

/** Русский плюрал «поле» для тоста сохранения. */
function pluralField(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'поле';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'поля';
  return 'полей';
}

export function MapPageV2() {
  // Sprint 2026-21 T13: Migrated from per-instance `createMapStore` to the
  // global V2 singleton (`useMapStore` from useMapStoreV2). Per-field
  // selectors avoid the new-object-every-render trap of `useMapStore()`
  // — each subscription re-renders only when its slice changes.
  const basemap = useMapStore((s) => s.basemap);
  const activeScope = useMapStore((s) => s.activeScope);
  const satelliteStripOpen = useMapStore((s) => s.satelliteStripOpen);
  const dispatch = useMapStore((s) => s.dispatch);
  // AK-383 #8: slices that key the active raster tile-load. When either changes
  // the `ndvi-active-raster` source refetches → we show a spinner on the strip.
  const activeRasterMode = useMapStore((s) => s.activeRasterMode);
  const selectedDate = useMapStore((s) => s.selectedDate);
  // AK-485: линза активного сезона на карте — свитчер сезона в шапке иначе
  // не долистывает до MVT-тайла вовсе (аудит crop-status-truth §3 R5b/Д1:
  // подпись карты и попап того же поля показывали культуру из РАЗНЫХ
  // сезонов). season_id меняет URL тайла → новый source для MapLibre (тот
  // же принцип, что и _v-timestamp при смене farm_id/cluster_id, см.
  // CLAUDE.md «Регрессии MVT-фильтра»). SQL-выборка тайла пока НЕ
  // параметризована по сезону (см. tiles.py) — карта = активный сезон
  // компании, произвольный сезон — этап ADR-059.
  const { season: activeSeason } = useActiveSeason();
  const activeSeasonId = activeSeason?.seasonId ?? null;

  const mapRef = useRef<maplibregl.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lmRef = useRef<LayerManager | null>(null);
  // AK-320: measure-контроллер (imperative, привязан к MapLibre). Создаётся
  // после styleReady, управляется из measureStore. measureActive нужен и в
  // handleFieldClick (подавить field-popup), и для mount'а MeasurePanel.
  const measureCtrlRef = useRef<MeasureController | null>(null);
  const measureActive = useMeasureStore((s) => s.active);
  // AK-321: contour-editor (imperative terra-draw, изолированный namespace).
  // editorActive нужен в handleFieldClick (клик по полю → traceField вместо
  // popup) и для mount'а ContourEditorPanel.
  const editorCtrlRef = useRef<ContourEditorController | null>(null);
  const editorActive = useEditorStore((s) => s.active);
  // AK-334: «+ Поле» tap-to-field. addFieldActive нужен для mount'а AddFieldPanel;
  // контроллер живёт в ref, управляется через applyAddFieldState по подписке.
  const addFieldCtrlRef = useRef<AddFieldController | null>(null);
  const addFieldActive = useAddFieldStore((s) => s.active);
  // AK-377 Track1: способ добавления. Sentinel-подложку автопоиска показываем
  // только когда активен ИМЕННО «Автопоиск» (detect) — рисование/файл работают
  // по чёткой ESRI, не замыливаем.
  const addFieldMethod = useAddFieldStore((s) => s.method);
  const addFieldSavedCount = useAddFieldStore((s) => s.savedCount);
  const addFieldPhase = useAddFieldStore((s) => s.phase);
  // AK-591: право создавать поля — гейт «сохранить нарисованное как новое
  // поле» на выходе из безвладельного редактора (тот же гейт, что у «+ Поле»
  // в RightRail).
  const canCreateField = useCan(PERM.fieldWrite, 'c');
  // AK-368c: контроллер превью импорта (af-import-* слои). Панель монтируется по
  // importPhase !== 'idle'; контроллер управляется через applyImportState.
  const importCtrlRef = useRef<ImportController | null>(null);
  const importPhase = useImportStore((s) => s.phase);
  // Индекс строки импорта, чью правку контура сейчас держит редактор (для
  // возврата уточнённой геометрии в пачку из prevEditorActive-эффекта).
  const importEditIndexRef = useRef<number | null>(null);
  // AK-590: ВЛАДЕНИЕ редактором контура. Ставится там же, где редактор
  // открывается для режима «+ Поле» (editSignal/manualDrawSignal), снимается на
  // выходе. Редактор один, а открывают его три пути (см. returnFromEditor.ts):
  // без этого гейта закрытие редактора «Обвести поле» кнопкой «+ Поле»
  // (RightRail гасит редактор и включает режим добавления одним обработчиком)
  // отдало бы контур СУЩЕСТВУЮЩЕГО поля в форму создания нового — дубль в БД.
  // Владение, а не фаза addFieldStore: фаза живёт в соседнем сторе и может
  // разойтись с реальностью, а ref ставит тот же эффект, что и открывает.
  const addFieldEditRef = useRef(false);
  // AK-591: в текущей сессии редактора загружался контур СУЩЕСТВУЮЩЕГО поля
  // (traceField по клику). Такой контур на выходе не уходит никуда; а вот
  // нарисованное С НУЛЯ в безвладельном редакторе («Обвести поле» → инструменты
  // рисования) «Готово» больше не выбрасывает — уводит в форму нового поля.
  const editorTracedRef = useRef(false);
  // AK-591 (rework по ревью архитектора): явная «Отмена» режима «+ Поле»
  // (drawing-хинт / тумблер рейла / Esc → af.deactivate()) гасит редактор,
  // НАМЕРЕННО выбросив черновик. Отличаем это от «редактор ничей» (null):
  // иначе спасение draw-save-as-new воскрешало бы отменённый контур формой.
  const editorDiscardRef = useRef(false);
  // Хозяйства + имена полей для ImportFilePanel (грузим лениво при входе в импорт).
  const [importFarms, setImportFarms] = useState<{ id: string; name: string }[]>([]);
  const [importExistingNames, setImportExistingNames] = useState<string[]>([]);
  // AK-369: отделения/бригады выбранного хозяйства (для формы сохранения).
  const [importBrigades, setImportBrigades] = useState<Brigade[]>([]);
  const importFarmId = useImportStore((s) => s.farmId);
  // Подсвеченная строка импорта (клик по строке ↔ контур на карте).
  const [importFocusIndex, setImportFocusIndex] = useState<number | null>(null);
  // Зеркало importFocusIndex в ref — контроллер читает актуальную подсветку без
  // пересоздания lifecycle-эффекта. importRefreshRef — реаплай applyImportState
  // при смене подсветки (focus-эффект зовёт его).
  const importFocusIndexRef = useRef<number | null>(null);
  const importRefreshRef = useRef<(() => void) | null>(null);
  // Последнее сырое кольцо из редактора (AK-334 «Готово» → уточнённый контур в
  // форму). Обновляется onRingChange редактора; читается при возврате из editing.
  const editedRingRef = useRef<LngLat[] | null>(null);
  // AK-371: последняя ПОЛНАЯ геометрия из редактора (Polygon | MultiPolygon) —
  // для сохранения дырок/несмежных частей. Приоритетнее editedRingRef при
  // возврате в форму. null пока фигуры нет.
  const editedGeometryRef = useRef<EditorGeometry | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [styleReady, setStyleReady] = useState(false);
  // «Посев с карты» (запрос founder 2026-07-27): цель портала для блока посева
  // внутри HTML-попапа поля. HTML-попап рисуется строкой (buildFieldPopoverHtml),
  // поэтому интерактивную часть монтируем React-порталом в его слот —
  // react-query / контексты сезона и прав работают как в обычном дереве.
  // null = попап закрыт (портал размонтирован).
  const [sowMount, setSowMount] = useState<{
    node: HTMLElement;
    fieldId: string;
    fieldName: string;
    crop: string | null;
    phase: string | null;
  } | null>(null);
  // AK-772: цель портала для блока ДЕЙСТВИЙ над полем (переименовать /
  // архивировать / геометрия) — тот же приём, что у sowMount выше.
  const [actionsMount, setActionsMount] = useState<{
    node: HTMLElement;
    fieldId: string;
    fieldName: string;
    /** Есть ли культура (из MVT-свойств) — предупреждение в диалоге архива. */
    hasCrop: boolean;
  } | null>(null);
  // Какое действие человек попросил. Диалоги рендерятся на уровне страницы,
  // поэтому переживают закрытие попапа (иначе ввод причины архивации
  // исчезал бы вместе с попапом).
  const [fieldAction, setFieldAction] = useState<{
    kind: 'rename' | 'archive';
    fieldId: string;
    fieldName: string;
    hasCrop: boolean;
  } | null>(null);
  const [archivePending, setArchivePending] = useState(false);
  // Текущий попап поля — чтобы закрыть его после архивации/переименования
  // (иначе попап продолжал бы утверждать про поле, которого уже нет).
  const fieldPopupRef = useRef<maplibregl.Popup | null>(null);
  // ОДИН отложенный перезапрос тайлов «после рефреша витрины подписей»
  // (review head-web #978): выверка — это СЕРИЯ правок, и N таймеров дали бы N
  // пересозданий MVT-источника, то есть N перекачек всех видимых тайлов.
  // Новая правка сбрасывает предыдущий таймер: серия стоит одного обновления.
  // Снимаем и на размонтировании — таймер, доживший до снятого стиля, дёргал
  // бы мёртвую карту.
  const mvLagTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (mvLagTimerRef.current !== null) {
        window.clearTimeout(mvLagTimerRef.current);
        mvLagTimerRef.current = null;
      }
    },
    [],
  );
  // Подтверждение «Посеяно: …» — overlay-тост карты (review head-web: общий
  // lib/toast.ts только пишет в консоль, <Toaster> нигде не смонтирован).
  // Тот же паттерн, что successToast в AddFieldPanel / ImportFilePanel:
  // pill top-center + авто-скрытие 4с.
  const [sowToast, setSowToast] = useState<string | null>(null);
  useEffect(() => {
    if (!sowToast) return;
    const t = window.setTimeout(() => setSowToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [sowToast]);
  // AK-388: fail-soft тост при отказе/ошибке геолокации (кнопка «моё
  // местоположение»). Сообщение из useGeolocateControl; авто-скрытие ниже.
  const [geoError, setGeoError] = useState<string | null>(null);
  // Sprint 2026-25 T-B2: «Аккаунт активирован» toast при redirect'е с
  // backend accept-invite handler'а в случае promoted=1 (consultant-creates-
  // proto magic-link flow). Параметр читается ОДИН раз on mount; затем
  // удаляется из URL чтобы F5 не показывал toast снова.
  const [promotedToast, setPromotedToast] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('promoted') === '1';
  });
  useEffect(() => {
    if (!promotedToast) return;
    // Strip ?promoted=1 from URL без reload (history API).
    const url = new URL(window.location.href);
    url.searchParams.delete('promoted');
    window.history.replaceState({}, '', url.toString());
    // Auto-dismiss через 6 секунд.
    const timer = window.setTimeout(() => setPromotedToast(false), 6000);
    return () => window.clearTimeout(timer);
  }, [promotedToast]);

  // AK-388: авто-скрытие fail-soft тоста геолокации через 6 секунд.
  useEffect(() => {
    if (!geoError) return;
    const timer = window.setTimeout(() => setGeoError(null), 6000);
    return () => window.clearTimeout(timer);
  }, [geoError]);

  // AK-368a: вход на карту с ?addField=1 (кнопка «Создать поле» в списке полей,
  // редирект со старого /fields/new) → сразу включаем режим «+ Поле». Ждём
  // styleReady (панель/контроллер add-field монтируются под styleReady), затем
  // activate() один раз и стираем параметр из URL, чтобы F5 не переактивировал.
  const addFieldAutoStartRef = useRef(false);
  // AK-584: заход из мастера первого входа (`&onboarding=1`) — после первого
  // сохранения показываем карточку «Готово» с подводкой про снимки (шаг 3).
  const [onboardingRun, setOnboardingRun] = useState(false);
  const [doneCardDismissed, setDoneCardDismissed] = useState(false);
  useEffect(() => {
    if (!styleReady || addFieldAutoStartRef.current) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('addField') !== '1') return;
    addFieldAutoStartRef.current = true;
    // AK-584: «добавить поле в ЭТО хозяйство» — вход из карточки хозяйства,
    // из дерева структуры или из мастера. Хозяйство подставляем в форму
    // заранее, чтобы её не спрашивали у человека, который уже стоит внутри
    // хозяйства. `farm` НЕ стираем из URL — его же читает восстановление
    // скоупа карты (AK-296).
    const presetFarm = params.get('farm');
    if (presetFarm) useAddFieldStore.getState().presetFarm(presetFarm);
    if (params.get('onboarding') === '1') setOnboardingRun(true);
    // AK-369 «одна дверь»: `?import=1` → сразу открыть загрузку файла (кнопка
    // «Импорт из файла» в списке полей ведёт на единый путь добавления).
    const withImport = params.get('import') === '1';
    // Взаимо-эксклюзив с линейкой/редактором (как в RightRail) — на входе с
    // карты они выключены, но гасим на всякий случай перед activate.
    useMeasureStore.getState().deactivate();
    useEditorStore.getState().deactivate();
    if (withImport) {
      useAddFieldStore.getState().activateWithImport();
    } else {
      useAddFieldStore.getState().activate();
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('addField');
    url.searchParams.delete('import');
    url.searchParams.delete('onboarding');
    window.history.replaceState({}, '', url.toString());
  }, [styleReady]);

  // v3 IA (карточка поля): вход на карту с `?field=<id>&lng=&lat=` — кнопка
  // «📍 На карте» в шапке карточки. Центрируем карту на центроиде поля (flyTo,
  // как в панели поиска) + выделяем поле, затем стираем параметры из URL, чтобы
  // F5 не пере-летал. Аддитивно к addField-эффекту выше; тот же styleReady-гейт.
  const focusFieldRef = useRef(false);
  useEffect(() => {
    if (!styleReady || focusFieldRef.current) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const fieldId = params.get('field');
    if (!fieldId) return;
    focusFieldRef.current = true;
    const lng = Number(params.get('lng'));
    const lat = Number(params.get('lat'));
    if (Number.isFinite(lng) && Number.isFinite(lat) && mapRef.current) {
      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: Math.max(mapRef.current.getZoom(), 15),
        duration: 700,
        essential: true,
      });
    }
    // Выделяем поле — тот же путь, что клик по полю / выбор в поиске.
    useMapStore.getState().dispatch({ type: 'SELECT_FIELD', fieldId });
    const url = new URL(window.location.href);
    url.searchParams.delete('field');
    url.searchParams.delete('lng');
    url.searchParams.delete('lat');
    window.history.replaceState({}, '', url.toString());
  }, [styleReady]);

  // Used by the click handler to take a sync snapshot of the global store
  // when computing ariaCtx. Reading via getState() outside React is the
  // documented pattern for imperative callbacks (zustand FAQ).
  const storeApi = useMapStore;

  // Wire the 4 declarative effects (spec §7.2-§7.5 / Sprint 2026-21 T4-T8).
  // Order is irrelevant — each subscribes to its own slice and is
  // independent. Effect 3 + 4 take the map ref; the hook short-circuits
  // when map is null (initial render) and re-runs once mapRef populates.
  useDatesEffect();
  useCoverageEffect();
  useLayerSyncEffect(mapRef.current);
  useRasterSourceEffect(mapRef.current);
  // AK-193 (ADR-037 #6 / audit F1): warm prev/next date tiles for the current
  // viewport so date-stepping renders warm instead of cold (blank→pop).
  usePrefetchDatesEffect(mapRef.current);
  // AK-104 founder UAT 2026-05-08: registers `crop-stripe-{id}` and
  // `crop-dots-{id}` pattern images so layers crop-fill-planned /
  // crop-fill-harvested in registry.ts can render hatched / dotted patterns.
  useCropPatternAtlasEffect(mapRef.current);
  // AK-299 / AK-303: registers the stretchable rounded-rect `field-pill-bg`
  // sprite so field-label pill layers (registry.ts §12.e/§12.f) can render the
  // pill-card background via icon-text-fit. Re-registers on theme change
  // (MapLibre can't resolve hsl(var(--token)) in sprites).
  usePillSpriteEffect(mapRef.current);
  // AK-377 Track1 «перцептивный фикс детекта»: пока активен способ «Автопоиск»
  // (method==='detect'), показываем поверх ESRI слой Sentinel-2 TCI (тот же, по
  // которому идёт detect) — обводка ложится на ту же картинку, что «видит»
  // алгоритм. На выход из автопоиска слой прячется. Только visibility → без
  // мерцания, teardown-safe (isMapAlive).
  useAutodetectBasemapSync(mapRef.current, addFieldActive && addFieldMethod === 'detect');
  // AK-388 «моё местоположение»: нативный GeolocateControl — ТОЛЬКО на мобиле
  // (iOS+Android), на десктопе не добавляется. Синяя точка + trackUserLocation.
  // Teardown-safe (isMapAlive внутри); отказ геолокации → fail-soft тост.
  useGeolocateControl(styleReady ? mapRef.current : null, styleReady, {
    onError: setGeoError,
  });

  // AK-383 #8: track raster tile-load so the satellite date strip can show a
  // spinner while COG tiles stream in after a date/product switch. Token = the
  // (mode, date) pair that keys the `ndvi-active-raster` refetch; null when no
  // raster overlay is active (nothing to load → no spinner). Only wire it once
  // `styleReady` so the source exists to observe. Reuses the same hook the
  // field-card mini-map uses (FieldOverviewTab) — single feedback mechanism.
  const rasterLoadToken =
    styleReady && activeRasterMode !== 'none' && selectedDate
      ? `${activeRasterMode}:${selectedDate}`
      : null;
  const satelliteTilesLoading = useRasterTilesLoading(
    styleReady ? mapRef.current : null,
    'ndvi-active-raster',
    rasterLoadToken,
  );

  // Hydrate: load persisted prefs once. Controls render only after this
  // resolves so user input cannot race the GET → hydrate overwrite.
  //
  // Bug #7 (Sprint 2026-19): seed `activeScope` from /api/v1/scope/available
  // current pointer — раньше map всегда стартовал с {company,null} даже если
  // пользователь выбрал farm в AppShell (переключатель «Хозяйство ▾» вверху
  // сайдбара, `FarmSwitcher`; до AK-895 — пилюля в шапке). После удаления
  // TopBarPill единственный селектор — в AppShell, и v2.tsx обязан
  // синхронизироваться через server-side scope state.
  //
  // Sprint 2026-21 T13: V2 reducer has only `HYDRATE` (PersistentMapState
  // subset). Server-side prefs may include session vars in the wire format,
  // but HYDRATE intentionally restores only persistent keys (lock-in #1).
  // `activeScope` is dispatched via SET_ACTIVE_SCOPE separately.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      // AK-629: настройки — НЕ гейт для стартового скоупа. `loadMapPrefs` не
      // reject'ит по контракту, `.catch` — второй пояс: если настройки всё же
      // упадут, `Promise.all` не должен унести с собой авто-выбор фермы, иначе
      // карта застрянет в company-скоупе и спрячет кнопку «Снимки» (AK-106).
      loadMapPrefs().catch(() => ({}) as Partial<MapState>),
      api
        .get<{ current: ScopeRef; available?: ScopeRef[] }>('/api/v1/scope/available')
        .catch((err) => {
          if (import.meta.env.DEV) console.warn('[mapV2] scope/available failed', err);
          else captureException(err, { tags: { source: 'mapV2.scopeAvailable' } });
          return null;
        }),
    ]).then(async ([prefs, scope]) => {
      if (cancelled) return;
      // HYDRATE pulls only PersistentMapState subset out of the wire body —
      // V2 reducer's HYDRATE handler ignores non-persistent fields by
      // construction (mapReducer §3.4).
      dispatch({ type: 'HYDRATE', prefs });
      // AK-296 (deep-link scope restore): the URL (`?farm=`/`?cluster=`) is the
      // PRIORITY scope input on mount — opening a shared map link must land on
      // the link's scope, not the server's last pointer. `persistScopeUrl`
      // already writes these params on chip-click; this reads them back. Fall
      // back to the server pointer (`/scope/available`) only when no scope param
      // is present.
      const urlScope = scopeFromUrl();
      // Кабинет с ОДНИМ хозяйством (прод 17.08.2026): company-скоуп прячет
      // кнопку «Снимки» (лок AK-106 в RightRail), а выбирать человеку нечего —
      // ферма одна. Порядок победителей и причина, почему авто-выбор НЕ
      // пишется на сервер (кросс-тенантная утечка active_scope), — в
      // pickInitialScope.ts.
      //
      // Окно клиента (прод 19.08.2026): у консультанта нет membership в
      // тенанте клиента → /scope/available отдаёт stub-company БЕЗ ферм и
      // первый пояс не срабатывает. Второй пояс спрашивает GET /farms —
      // подробности и границы в resolveInitialScopeWithFarmFallback.
      const { scope: initialScope, autoSelectedFarm } = await resolveInitialScopeWithFarmFallback(
        {
          urlScope,
          current: scope?.current ?? null,
          available: scope?.available,
        },
        () => api.get<Array<{ id: string }>>('/api/v1/farms'),
      );
      if (cancelled) return;
      // Диагностика стартового скоупа — всегда, не только в DEV. Staging
      // 20.08 (окно Зелюки без кнопки «Снимки»): по симптому неразличимо, ЧТО
      // именно не сработало — гард поясов, пустой ответ /scope/available или
      // «не ровно одна ферма» в /farms. Одна строка на загрузку карты
      // закрывает вопрос из консоли, без пересборки и без Sentry-шума.
      console.info(
        '[mapV2] стартовый скоуп:',
        initialScope ? `${initialScope.type}:${initialScope.id ?? '—'}` : 'дефолт store (company)',
        autoSelectedFarm ? '· ферма выбрана автоматически (локально, без /scope/set)' : '',
        `· current=${scope?.current ? `${scope.current.scope_type}:${scope.current.scope_id ?? '—'}` : 'нет ответа'}`,
        `· ферм в available=${(scope?.available ?? []).filter((s) => s.scope_type === 'farm' && s.scope_id).length}`,
        urlScope ? `· URL=${urlScope.type}:${urlScope.id ?? '—'}` : '',
      );
      if (initialScope) {
        dispatch({ type: 'SET_ACTIVE_SCOPE', scope: initialScope });
        // AK-102: fitBounds on initial scope hydrate. Wait one frame for
        // mapRef to populate (MapLibre instance created in onMount further
        // down; race-safe via requestAnimationFrame loop).
        const tryFit = (attemptsLeft: number) => {
          if (!mapRef.current) {
            if (attemptsLeft > 0) {
              requestAnimationFrame(() => tryFit(attemptsLeft - 1));
            }
            return;
          }
          void fitBoundsToScope(mapRef.current, initialScope);
        };
        tryFit(60); // ~1 second worth of frames
      }
      setHydrated(true);
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Bug #7 (Sprint 2026-19): sync map's activeScope when user switches scope
  // in AppShell (`FarmSwitcher` вверху сайдбара → `useScopeSetter` →
  // dispatchScopeChanged → window event 'ikolhoz:scope-changed'). Раньше
  // v2.tsx не слушал → user менял scope в шапке, но MVT/raster URLs
  // продолжали показывать старый.
  //
  // AK-102 (founder UAT 2026-05-08): после dispatch SET_ACTIVE_SCOPE
  // карта оставалась на текущем viewport и не перескакивала на bbox
  // выбранного хозяйства. Раньше fitBounds жил в ScopeDrawer (deleted
  // в Sprint 2026-21 V2). Восстановлено here: fetch /scope/extents,
  // конвертируем EPSG:3857 → 4326, fitBounds с padding 80 + animate 600ms.
  useScopeChanged((detail) => {
    const scope: ActiveScope = { type: detail.scope_type, id: detail.scope_id };
    dispatch({ type: 'SET_ACTIVE_SCOPE', scope });
    void fitBoundsToScope(mapRef.current, scope);
  });

  // Cross-device server-side persist (debounced). V2 store also persists to
  // localStorage via zustand middleware — this layer is just for cross-device
  // sync. Subscribed to the global store via storeApi.subscribe so we get
  // every dispatch without forcing the component to re-render on each.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = storeApi.subscribe((s) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        // persistMapPrefs не reject'ит по контракту (AK-629): 404 гасит запись
        // до конца загрузки страницы, сеть/5xx проглатываются молча. `.catch`
        // оставлен как страховка от будущих правок модуля.
        void persistMapPrefs(s as unknown as MapState).catch((err) => {
          if (import.meta.env.DEV) console.warn('[mapV2] persistPrefs failed', err);
          else captureException(err, { tags: { source: 'mapV2.persistPrefs' } });
        });
      }, 1000);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, [storeApi]);

  // AK-106 (Sprint #2 2026-05-08): MUST wait for `hydrated` before creating
  // the MapLibre instance. Otherwise the initial `fields-mvt` source is built
  // with INITIAL_MAP_STATE.activeScope = {company,null} and tile requests
  // fly without `farm_id` even when backend session has farm scope.
  //
  // Race trace before fix:
  //   1. Mount → useEffect creates map с tiles=[buildMvtUrl({company,null})] → no farm_id
  //   2. Style loads → setStyleReady(true) → MapLibre fetches viewport tiles
  //      (no farm_id → backend L-001 violation, returns ALL company fields)
  //   3. /scope/available resolves → dispatch SET_ACTIVE_SCOPE({farm, ec1...})
  //   4. setSourceTiles('fields-mvt', buildMvtUrl(farm)) called — BUT tiles
  //      already in maxTileCacheSize=512 cache and viewport already painted.
  //      MapLibre's setTiles() invalidates pending fetches but cached/rendered
  //      tiles persist until pan/zoom invalidates them.
  //
  // Fix: gate map creation on `hydrated`. Initial tiles[] then includes the
  // correct scope from the very first request → no audit violations, no
  // cross-farm leak, no setTiles cache-eviction edge cases.
  useEffect(() => {
    if (!containerRef.current) return;
    if (!hydrated) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        // AK-299: glyphs URL is required for ANY symbol layer with `text-field`
        // to render text (field-label pills, scope-centroid labels, arrays
        // pills). The V2 style rewrite dropped the `glyphs` key that legacy
        // MapCanvas had → symbol text could not be shaped. Same demotiles
        // fontstack the legacy map uses (provides "Open Sans"/"Noto Sans").
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
        sources: {
          basemap: {
            type: 'raster',
            tiles: [basemapUrls()[storeApi.getState().basemap]],
            tileSize: 256,
            attribution: 'Esri',
          },
          // Bug C (Sprint 2026-19 Day 2): transport/labels overlay для hybrid.
          // Source всегда зарегистрирован, но соответствующий layer
          // (`basemap-transport`, z=1.5) виден только при s.basemap === 'hybrid'.
          'basemap-transport': {
            type: 'raster',
            tiles: [TRANSPORT_TILE_URL()],
            tileSize: 256,
            attribution: 'Esri Reference',
          },
          // Founder UAT 2026-05-08: hybrid mode missing place names. Add
          // World_Boundaries_and_Places overlay (admin borders + city/town
          // labels, transparent). Layer (`basemap-places`, z=1.6) активен
          // только при s.basemap === 'hybrid' через registry visibility.
          'basemap-places': {
            type: 'raster',
            tiles: [PLACES_TILE_URL()],
            tileSize: 256,
            attribution: 'Esri Reference',
          },
          'fields-mvt': {
            type: 'vector',
            // Bug #2 (Sprint 2026-19): инициализируем с scope query-param —
            // backend filter без него возвращает None или 500.
            // AK-485: + season_id (best-effort на момент mount'а — если
            // SeasonContext ещё не резолвился, догонит эффект ниже [styleReady,
            // activeScope, activeSeasonId], который пересоздаёт source).
            tiles: [buildMvtUrl(storeApi.getState().activeScope, activeSeasonId)],
            minzoom: 7,
            maxzoom: 18,
          },
          // Sprint 2026-26: scope_centroids vector source — visual labels
          // в центре scope-границы (holding/company/cluster/farm/brigade).
          // Founder UAT 2026-05-08 lock-in: text-only label, не интерактивный
          // селектор; backend per-zoom фильтр scope_type'ов в /tiles/scope-
          // centroids/{z}/{x}/{y}.mvt — minzoom 6 (z<6 backend возвращает 204).
          //
          // Перф-аудит 14.08 (корень 5): за сессию — 1638 запросов к этому
          // источнику, 613 уникальных URL, 916 оборвано клиентом (499).
          // Два лечения, оба без потери картинки:
          //
          //  1. `maxzoom: 14` вместо 18. Бэкенд отдаёт ОДИН И ТОТ ЖЕ набор
          //     scope_type'ов на всех z ≥ 14 (tiles.py: холдинг+компания+
          //     кластер+ферма+бригада), то есть тайлы z15-18 были побайтово
          //     эквивалентны z14. Теперь MapLibre их оверзумит из z14 —
          //     четыре зум-уровня запросов исчезают, подписи те же.
          //
          //  2. Про 13 повторов ОДНОГО URL. Cache-buster'а тут нет и не было —
          //     URL статичный. Повторы порождает связка «пустой тайл + expiry»:
          //     центроидов на тенант считанные точки, поэтому подавляющее
          //     большинство тайлов пустые, бэкенд отвечает 204, а 204 браузер
          //     в HTTP-кэш не кладёт; MapLibre по истечении `max-age`
          //     перезапрашивает тайл заново. Точечно выключить это нельзя:
          //     `refreshExpiredTiles` в MapLibre — опция КАРТЫ, не источника, а
          //     глушить её на всю карту запрещено (CLAUDE.md «Регрессии
          //     MVT-фильтра»: залипают тайлы полей при смене хозяйства).
          //     Лечим с двух других сторон — срок жизни пустого тайла поднят с
          //     60 с до 300 с (tiles.py), а сами повторы теперь снимает
          //     edge-кэш nginx (deploy/nginx/*.conf), а не Postgres.
          'scope-centroids-mvt': {
            type: 'vector',
            tiles: [
              `${window.location.origin}/api/v1/tiles/scope-centroids/{z}/{x}/{y}.mvt`,
            ],
            minzoom: 6,
            maxzoom: 14,
          },
          // Sprint 2026-21 hotfix (post-merge): pre-register all 3 raster sources
          // with EMPTY_TILE так чтобы LayerManager.init() мог добавить layers
          // ссылающиеся на эти sources. Effects (rasterSourceEffect) затем swap'ят
          // tiles[] через setTiles() / removeSource+addSource.
          // Без pre-registration падает: "source ndvi-active-raster not found".
          //
          // AK-193: + minzoom/maxzoom (overzoom, was missing — audit Q3/F3) and
          // flag-driven tileSize (256 default; 512 = ¼ requests once the backend
          // serves 512px tiles — see featureFlags.rasterTileSize). The recreate
          // path (recreateRasterSource) reads the same flag so a scope-switch
          // re-registers with identical bounds.
          'ndvi-active-raster': {
            type: 'raster',
            tiles: [EMPTY_TILE],
            tileSize: rasterTileSize(),
            minzoom: RASTER_MINZOOM,
            maxzoom: RASTER_MAXZOOM,
          },
          'rgb-scene-raster': {
            type: 'raster',
            tiles: [EMPTY_TILE],
            tileSize: rasterTileSize(),
            minzoom: RASTER_MINZOOM,
            maxzoom: RASTER_MAXZOOM,
          },
          'cloud-mask-raster': {
            type: 'raster',
            tiles: [EMPTY_TILE],
            tileSize: rasterTileSize(),
            minzoom: RASTER_MINZOOM,
            maxzoom: RASTER_MAXZOOM,
          },
        },
        layers: [],
      },
      center: [40, 50],
      zoom: 7,
      // Performance tuning:
      // - maxTileCacheSize: больше tiles в RAM (default ~50/source), меньше
      //   повторных fetch при back-pan / zoom out-in. 512 даёт ~85 tiles
      //   per source при 6 sources — comfortable для прохождения по карте
      //   без re-fetch соседних tiles.
      // - fadeDuration=0: убирает 300ms fade-in анимацию, instant render —
      //   perceptually faster (особо заметно при scope/date switch).
      maxTileCacheSize: 512,
      fadeDuration: 0,
    });
    mapRef.current = map;
    // Expose map instance for E2E tests (Playwright evaluate access).
    // Always exposed (not gated by DEV/test) — required for browser smoke
    // harness against staging/prod builds. Single global var, не security-
    // sensitive (MapLibre instance public API, никаких credentials).
    (window as unknown as Record<string, unknown>).__mapInstance = map;

    // WHITE-CANVAS FIX (V2 first-load): re-attach the resize protection that
    // legacy MapCanvas always had but this V2 rewrite dropped. The map is
    // created gated on `hydrated`; the 2FA banner (StackedBanners in AppShell)
    // mounts only after `/auth/me` resolves, often AFTER the map exists, and
    // shrinks <main>'s height (adaptive-layout grabля, AK-33). With no
    // ResizeObserver the GL canvas keeps a stale backing-store size and the
    // render loop parks without compositing → blank white canvas
    // (isStyleLoaded=false, isMoving stuck true) until a manual map.resize().
    // installMapResizeGuard observes the container + forces a resize after
    // `load`, so first paint always matches the settled container size.
    const disposeResizeGuard = installMapResizeGuard(
      map,
      containerRef.current,
    );
    map.on('load', () => {
      const lm = new LayerManager(map, LAYER_REGISTRY);
      lm.init();
      lm.applyState(storeApi.getState());
      lmRef.current = lm;
      setStyleReady(true);

      // Stage 3 — native HTML field popover on click of contour layers.
      // See spec §6 + docs/superpowers/specs/map-redesign-stage-3.md.
      //
      // Bug A (Sprint 2026-19 Day 2): click handler ранее (Bug #9 fix)
      // навешивался на outline-planting + outline-reference. Founder
      // повторяет: «click on field — ничего не происходит». Корень
      // проблемы — MapLibre `queryRenderedFeatures` под layer-filter
      // требует pixel-perfect попадания в 1.6px line, и при `visibility:
      // none` слой исключается из hit-test. Решение: ввести скрытый fill
      // слой `field-click-target` (z=11.5, opacity 0, visibility 'always')
      // в registry — wide click target над всем полигоном поля.
      // Здесь подписываемся на click ТОЛЬКО этого слоя — он всегда жив,
      // всегда покрывает поле.
      const handleFieldClick = (e: maplibregl.MapLayerMouseEvent) => {
        // AK-320: при активной линейке клики по полю идут в measure-контроллер
        // (ставят точки), а не открывают field-popup. Ранний return подавляет
        // popup, не трогая остальную логику слоя.
        if (useMeasureStore.getState().active) return;
        // AK-321: при активном редакторе клик по полю → «обвести поле»
        // (preload его контура как editable). Popup не открываем.
        if (useEditorStore.getState().active) {
          const fid = (e.features?.[0]?.properties as FieldPopoverProps | undefined)?.id;
          if (fid) {
            // AK-591: сессия обвела существующее поле — его контур на выходе
            // из редактора не уходит никуда (гейт в decideReturnFromEditor).
            editorTracedRef.current = true;
            void editorCtrlRef.current?.traceField(fid);
          }
          return;
        }
        if (!e.features?.length) return;
        const props = e.features[0].properties as FieldPopoverProps;
        // Sprint 2026-21 Task 12 / NFR-8 — compose ARIA context from V2 store
        // snapshot. Coverage lookup uses the V2 single-store cache key
        // (encoding mirrored from coverageEffect.ts T5 / deriveCoveredIds T7):
        //   `${scope.type}:${scope.id ?? 'null'}:${product}:${date}`
        // Snapshot at click time — popup HTML статичен после setHTML, поэтому
        // ARIA не реактивна на последующие изменения coverage.
        const snap = storeApi.getState();
        const product = productOf(snap.activeRasterMode);
        let hasCoverage = false;
        if (product && snap.selectedDate && props.id) {
          const key = `${snap.activeScope.type}:${snap.activeScope.id ?? 'null'}:${product}:${snap.selectedDate}`;
          hasCoverage = snap.coverage.get(key)?.coveredFieldIds.has(props.id) ?? false;
        }
        const ariaCtx = {
          rasterMode: snap.activeRasterMode,
          selectedDate: snap.selectedDate,
          hasCoverage,
        };
        // Bug A2 (Sprint 2026-19 Day 2 v2): dispatch SELECT_FIELD —
        // reducer обновляет state.selectedFieldId, useLayerSyncEffect (T6)
        // apply'ит filter на field-selected-highlight layer (жёлтый контур).
        // Раньше click открывал popup но selectedFieldId оставался null,
        // и слой highlight не было visible.
        // Sprint 2026-21 T12: V2 reducer использует `SELECT_FIELD` /
        // `CLEAR_SELECTED_FIELD` (legacy V1 had `SET_SELECTED_FIELD`).
        // AK-295: MapLibre default closeButton OFF — рисуем свой DS-styled
        // крестик внутри HTML-шапки (.fp-close), чтобы убрать stray-артефакт
        // («красная 1») поверх дефолтного крестика и держать единый DS-язык.
        // AK-372: координаты + маршрут в навигатор. V1 точка = место клика
        // внутри полигона поля (валидная destination-точка; центроид как
        // отдельное развитие). Кнопки провайдеров несут app+web deep-link'и
        // в data-атрибутах — вешаем click → openNavigator.
        const coords = { lat: e.lngLat.lat, lng: e.lngLat.lng };
        // sowSlot: вместо статичной строки «Посев» из MVT-свойств попап
        // отдаёт пустой слот — в него ниже монтируется живой FieldSowBlock
        // (сезон/зоны + кнопка «Отметить: посеяно»). Утверждение о посеве на
        // экране остаётся одно, но становится актуальным и кликабельным.
        const popup = new maplibregl.Popup({ closeButton: false, closeOnClick: true, maxWidth: '300px' })
          .setLngLat(e.lngLat)
          // sowSlot только когда есть id поля: без него портал не смонтируется
          // и попап остался бы вообще без утверждения о посеве.
          .setHTML(
            buildFieldPopoverHtml(props, ariaCtx, coords, {
              sowSlot: Boolean(props.id),
              // AK-772: слот действий — тоже только когда есть id поля:
              // переименовывать/архивировать нечего без идентификатора.
              actionsSlot: Boolean(props.id),
            }),
          )
          .addTo(map);
        fieldPopupRef.current = popup;
        popup.on('close', () => {
          if (fieldPopupRef.current === popup) fieldPopupRef.current = null;
        });
        const popupEl = popup.getElement();
        const sowSlot = popupEl?.querySelector<HTMLElement>(
          '[data-testid="field-popover-sow-slot"]',
        );
        if (sowSlot && props.id) {
          setSowMount({
            node: sowSlot,
            fieldId: props.id,
            fieldName: props.label_number ?? 'поле',
            crop: props.label_crop ?? null,
            phase: props.current_phase ?? null,
          });
          // Закрылся попап — снимаем портал. Сверяем узел: клик по СОСЕДНЕМУ
          // полю сначала открывает новый попап, и только потом закрывается
          // старый (closeOnClick) — без сверки старый «close» стёр бы уже
          // смонтированный блок нового поля (та же защита, что у selection ниже).
          popup.on('close', () => {
            setSowMount((prev) => (prev?.node === sowSlot ? null : prev));
          });
        }
        // AK-772: тот же портал-приём для блока действий над полем.
        const actionsSlot = popupEl?.querySelector<HTMLElement>(
          '[data-testid="field-popover-actions-slot"]',
        );
        if (actionsSlot && props.id) {
          setActionsMount({
            node: actionsSlot,
            fieldId: props.id,
            fieldName: props.label_number ?? 'поле',
            hasCrop: Boolean(props.label_crop),
          });
          // Сверка узла — та же защита, что у sowMount: клик по соседнему полю
          // сначала открывает новый попап и только потом закрывает старый.
          popup.on('close', () => {
            setActionsMount((prev) => (prev?.node === actionsSlot ? null : prev));
          });
        }
        // Wire наш .fp-close к popup.remove() (default closeButton выключен).
        popupEl
          ?.querySelector('[data-testid="field-popover-close"]')
          ?.addEventListener('click', () => popup.remove());
        // AK-372: маршрут-кнопки → openNavigator (app-схема на мобайле,
        // web-фолбэк на десктопе). Читаем app/web URL из data-атрибутов.
        popupEl?.querySelectorAll<HTMLButtonElement>('.fp-nav-btn').forEach((btn) => {
          btn.addEventListener('click', () => {
            const app = btn.dataset.app;
            const web = btn.dataset.web;
            if (app && web) openNavigator({ app, web });
          });
        });
        // AK-373 п.7 / AK-380: primary «Маршрут» → openRoute. Android →
        // geo:-chooser; десктоп → web; iOS → Яндекс Карты дефолтом (БОЛЬШЕ не
        // share-sheet), а ряд «Открыть в:» ниже = in-app меню провайдеров
        // (Яндекс Карты / 2ГИС / Google / Apple).
        popupEl
          ?.querySelector<HTMLButtonElement>('.fp-route-btn')
          ?.addEventListener('click', (ev) => {
            const btn = ev.currentTarget as HTMLButtonElement;
            const lat = Number(btn.dataset.lat);
            const lng = Number(btn.dataset.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) openRoute(lat, lng);
          });
        if (props.id) {
          const fieldId = props.id;
          storeApi.getState().dispatch({ type: 'SELECT_FIELD', fieldId });
          // Когда popup закрывается — снимаем selection чтобы yellow контур
          // тоже исчез (closeOnClick=true либо ✕). UX: highlight живёт ровно
          // пока открыт popup о поле.
          popup.on('close', () => {
            // Защита: если за это время user кликнул на другое поле,
            // его id мог уже стать selectedFieldId — не сбрасываем чужой.
            if (storeApi.getState().selectedFieldId === fieldId) {
              storeApi.getState().dispatch({ type: 'CLEAR_SELECTED_FIELD' });
            }
          });
        }
      };
      map.on('click', 'field-click-target', handleFieldClick);
      // Pointer cursor for affordance — discoverability of click target.
      map.on('mouseenter', 'field-click-target', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'field-click-target', () => {
        map.getCanvas().style.cursor = '';
      });
    });
    return () => {
      disposeResizeGuard();
      lmRef.current?.destroy();
      map.remove();
      mapRef.current = null;
      lmRef.current = null;
      setStyleReady(false);
    };
    // AK-106: `hydrated` is the gate (prevents tile-without-farm_id race —
    // see comment above). storeApi is the V2 singleton; included for
    // exhaustive-deps compliance (stable reference, no re-run from it).
  }, [storeApi, hydrated]);

  // Apply state changes to layer visibility (declarative predicates per
  // spec §6.1, §6.4 — Sprint 2026-21 T6). Subscribe to the global store
  // imperatively — every dispatch re-applies. We read fresh state from
  // storeApi.getState() to avoid binding the layer-apply re-render to a
  // particular slice; the registry's visibility predicates may key off
  // any field of MapState.
  useEffect(() => {
    if (!styleReady) return;
    // Apply once on mount (covers the case where styleReady flips to true
    // after init).
    lmRef.current?.applyState(storeApi.getState());
    const unsub = storeApi.subscribe((s) => {
      lmRef.current?.applyState(s as unknown as MapState);
    });
    return unsub;
  }, [styleReady, storeApi]);

  // Bug #3 (Sprint 2026-19): basemap source URL swap on state.basemap change.
  // Раньше basemap инициализировался один раз и переключатель не реагировал.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    setSourceTiles(map, 'basemap', basemapUrls()[basemap]);
  }, [styleReady, basemap]);

  // ============================================================
  // AK-320 «Линейка на карте» — measure-контроллер lifecycle.
  //
  // Создаём контроллер один раз после styleReady и подписываемся на
  // measureStore. active → activate(mode), смена mode при активном →
  // setMode (очищает рисунок), !active → deactivate (снимает слушатели,
  // чистит оверлей, возвращает курсор). Результат рисования стекает в
  // measureStore.setResult → MeasurePanel показывает га/расстояние.
  //
  // Изоляция: контроллер добавляет свои measure-* source/layers ПОВЕРХ
  // (без beforeId), не трогает registry/LayerManager — field-слои и pills
  // не задеваются.
  // ============================================================
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    const ctrl = new MeasureController(map, (r) => {
      useMeasureStore.getState().setResult(r);
    });
    measureCtrlRef.current = ctrl;

    const init = useMeasureStore.getState();
    // Применить текущее состояние сразу (на случай active=true до подписки).
    //
    // РЕ-ВХОДИМОСТЬ (P0 fix): side-effect'ы контроллера (clear/deactivate/…)
    // СИНХРОННО зовут onResult → setResult → set({result}) → это снова дёргает
    // подписку → re-entrant apply(). Логика реордера prev*-снапшота (обновить
    // ДО side-effect'ов) вынесена в чистый applyMeasureState() и покрыта
    // регресс-тестом — см. applyMeasureState.ts + measure-reentrancy.test.ts.
    const prev = initMeasurePrev(init);
    applyMeasureState(prev, init, ctrl);
    const unsub = useMeasureStore.subscribe((s) => applyMeasureState(prev, s, ctrl));

    return () => {
      unsub();
      ctrl.deactivate();
      measureCtrlRef.current = null;
    };
  }, [styleReady]);

  // ============================================================
  // AK-321 «Редактор контура — обвести поле» — terra-draw lifecycle.
  //
  // Создаём контроллер один раз после styleReady и подписываемся на
  // editorStore. active → activate() (terra-draw start, select-mode), !active →
  // deactivate() (terra-draw stop — дерегистрирует adapter, убирает td-* слои).
  // clearSignal-инкремент → ctrl.clear() (кнопка «Очистить»).
  //
  // Изоляция (ADR-038): terra-draw рендерит в namespace `td-edit-` (adapter
  // prefixId) — отдельные source/layers, НЕ field-mvt/measure/registry. НЕ
  // подключены к recreateMvtSource → scope-смена полей не задевает редактор.
  // ============================================================
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    const ctrl = new ContourEditorController(map, {
      onResult: (r) => useEditorStore.getState().setResult(r),
      onHasFeature: (h) => useEditorStore.getState().setHasFeature(h),
      onSnapping: (s) => useEditorStore.getState().setSnapping(s),
      onLoading: (l) => useEditorStore.getState().setLoading(l),
      onError: (e) => useEditorStore.getState().setError(e),
      onHistoryChange: (h) => useEditorStore.getState().setHistory(h),
      // AK-334: держим ПОСЛЕДНЕЕ НЕПУСТОЕ кольцо для возврата из редактора в
      // форму. НЕ обнуляем на null: editor.deactivate() шлёт onRingChange(null)
      // синхронно перед тем, как наш prevEditorActive-эффект прочитает ref —
      // храня последнее валидное кольцо, мы переживаем этот teardown.
      onRingChange: (ring) => {
        if (ring && ring.length >= 4) editedRingRef.current = ring;
      },
      // AK-371: держим ПОСЛЕДНЮЮ НЕПУСТУЮ полную геометрию (мультиполигон/дырки)
      // для возврата в форму. Как и onRingChange, НЕ обнуляем на null —
      // deactivate() шлёт null синхронно перед чтением ref в return-эффекте.
      onGeometryChange: (geometry) => {
        if (geometry) editedGeometryRef.current = geometry;
      },
      // AK-371 v2: состояние набора фигур (сколько, можно ли резать, режим
      // выреза, активный инструмент) → в стор для доступности кнопок панели.
      onShapeState: (st) => useEditorStore.getState().setShapeState(st),
    });
    editorCtrlRef.current = ctrl;

    let prevActive = false;
    let prevFinishDraw = useEditorStore.getState().finishDrawSignal;
    let prevClear = useEditorStore.getState().clearSignal;
    let prevUndo = useEditorStore.getState().undoSignal;
    let prevRedo = useEditorStore.getState().redoSignal;
    let prevDraw = useEditorStore.getState().drawSignal;
    let prevUnion = useEditorStore.getState().unionSignal;
    let prevCut = useEditorStore.getState().cutSignal;
    let prevCircleMode = useEditorStore.getState().circleMode;
    const apply = (s: {
      active: boolean;
      clearSignal: number;
      undoSignal: number;
      redoSignal: number;
      drawSignal: number;
      unionSignal: number;
      cutSignal: number;
      finishDrawSignal: number;
      circleMode: import('@/lib/map/measure/measureStore').CircleMode;
    }) => {
      // РЕ-ВХОДИМОСТЬ (та же грабля, что в measure-контроллере, ADR-038):
      // ctrl.clear()/undo()/redo()/… синхронно зовут emitHistoryState /
      // emitShapeState → set() → подписка дёргается снова. Если prev* обновить
      // ПОСЛЕ side-effect'ов, вложенный apply увидит старый сигнал → повторит
      // операцию (напр. откатит всю историю за клик). Поэтому: захватываем
      // старые prev* в локали, СРАЗУ перезаписываем prev*, и ТОЛЬКО ПОТОМ дёргаем
      // side-effect'ы — вложенный apply увидит prev == next → no-op.
      const pActive = prevActive;
      const pClear = prevClear;
      const pUndo = prevUndo;
      const pRedo = prevRedo;
      const pDraw = prevDraw;
      const pUnion = prevUnion;
      const pCut = prevCut;
      const pFinishDraw = prevFinishDraw;
      const pCircleMode = prevCircleMode;
      prevActive = s.active;
      prevFinishDraw = s.finishDrawSignal;
      prevClear = s.clearSignal;
      prevUndo = s.undoSignal;
      prevRedo = s.redoSignal;
      prevDraw = s.drawSignal;
      prevUnion = s.unionSignal;
      prevCut = s.cutSignal;
      prevCircleMode = s.circleMode;

      if (s.active && !pActive) {
        ctrl.activate();
      } else if (!s.active && pActive) {
        ctrl.deactivate();
      }
      // Режим круга — держим синхронным до старта рисования (влияет на buildRing).
      if (s.circleMode !== pCircleMode) {
        ctrl.setCircleMode(s.circleMode);
      }
      if (s.clearSignal !== pClear) {
        ctrl.clear();
      }
      if (s.undoSignal !== pUndo) {
        ctrl.undo();
      }
      if (s.redoSignal !== pRedo) {
        ctrl.redo();
      }
      // AK-371 v2: начать рисовать фигуру (добавит к набору).
      if (s.drawSignal !== pDraw) {
        const tool = useEditorStore.getState().drawTool;
        if (tool) {
          ctrl.setCircleMode(useEditorStore.getState().circleMode);
          ctrl.beginShape(tool);
        }
      }
      // AK-371 v2: объединить все фигуры.
      if (s.unionSignal !== pUnion) {
        ctrl.unionShapes();
      }
      // AK-371 v2: войти/выйти из режима «Вырезать».
      if (s.cutSignal !== pCut) {
        ctrl.toggleCut();
      }
      // «Готово» на незамкнутом контуре: замыкаем сами (стандарт ГИС).
      // Не получилось — точек меньше трёх; говорим об этом и НЕ закрываем
      // редактор, иначе работа человека исчезала бы без единого слова.
      if (s.finishDrawSignal !== pFinishDraw) {
        const outcome = ctrl.finishDrawing();
        if (outcome !== 'closed') {
          useEditorStore.getState().setError(FINISH_DRAW_MESSAGE[outcome]);
        }
      }
    };
    apply(useEditorStore.getState());
    const unsub = useEditorStore.subscribe((s) => apply(s));

    return () => {
      unsub();
      ctrl.deactivate();
      editorCtrlRef.current = null;
    };
  }, [styleReady]);

  // ============================================================
  // AK-334/335 «Tap-to-field» — режим «+ Поле» lifecycle.
  //
  // Контроллер (курсор-прицел + тап + пульс + amber-черновик) создаётся один
  // раз после styleReady; управляется через applyAddFieldState по подписке
  // (re-entrancy-safe, тот же паттерн, что measure/editor — ADR-038).
  //
  // Тап по свободному месту → onDetectPoint → beginDetect + async delineate.
  // Тап по существующему полю → onExistingField (no-op): штатный field-click-
  // target handler откроет карточку поля (детект не зовём).
  //
  // Изоляция: свои af-* source/layers, editor/measure/fields-mvt не трогаем.
  // ============================================================
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;

    const store = useAddFieldStore.getState;

    const runDetect = async (point: LngLat) => {
      // AK-373: тап запускает автопоиск границ ТОЛЬКО в способе «Автопоиск».
      // В «Нарисовать»/«Файл» тап по карте не должен детектить (иначе способ
      // «скачет» — грабля п.4/6). Рисование/импорт живут в редакторе/дропзоне.
      if (store().method !== 'detect') return;
      // AK-368c: пока идёт импорт из файла (лоадер/превью/сохранение), тап по
      // карте НЕ запускает детект границ — пользователь работает с пачкой, а не
      // добавляет одиночное поле.
      if (useImportStore.getState().phase !== 'idle') return;
      store().beginDetect(point);
      try {
        const res = await delineateField(point);
        // Пользователь мог выйти из режима / начать заново пока крутился детект.
        if (!store().active || store().phase !== 'detecting') return;
        if (res.found) {
          store().setDraft(res.geometry, res.area_ha);
        } else {
          store().failDetect(
            'Поле не распознано — тапните ближе к центру или нарисуйте вручную',
          );
        }
      } catch (err) {
        if (!store().active) return;
        const msg =
          err instanceof DelineateError
            ? 'Поле не распознано — тапните ближе к центру или нарисуйте вручную'
            : 'Не удалось распознать поле. Попробуйте ещё раз.';
        store().failDetect(msg);
      }
    };

    const ctrl = new AddFieldController(map, {
      onDetectPoint: (point) => void runDetect(point),
      // Тап по существующему полю — детект не зовём. Открытие карточки поля
      // делает штатный field-click-target handler (он не подавлен при add-field).
      onExistingField: () => {},
    });
    addFieldCtrlRef.current = ctrl;

    const prev = initAddFieldPrev(store());
    applyAddFieldState(prev, store(), ctrl);
    const unsub = useAddFieldStore.subscribe((s) => applyAddFieldState(prev, s, ctrl));

    return () => {
      unsub();
      ctrl.destroy();
      addFieldCtrlRef.current = null;
    };
  }, [styleReady]);

  // ============================================================
  // AK-368c «Импорт полей из файла» — ImportController lifecycle.
  //
  // Контроллер (af-import-* amber-пунктирные слои превью) создаётся один раз
  // после styleReady; управляется через applyImportState по подписке (re-
  // entrancy-safe, тот же паттерн, что addField/measure/editor — ADR-038).
  //
  // Изоляция: свои af-import-* source/layers; editor/measure/addField/fields-mvt
  // не трогаем. destroy() гейтит isMapAlive (teardown после map.remove()).
  // ============================================================
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    const ctrl = new ImportController(map);
    importCtrlRef.current = ctrl;

    const snapshot = () => {
      const s = useImportStore.getState();
      return {
        phase: s.phase,
        rows: s.rows,
        highlightIndex: importFocusIndexRef.current,
        editingIndex: s.editingIndex,
      };
    };
    const prev = initImportPrev();
    applyImportState(prev, snapshot(), ctrl);
    // Подписка на стор + на смену подсветки (importFocusIndex через ref, чтобы
    // не пересоздавать контроллер). focus-эффект ниже дёргает refresh().
    const refresh = () => applyImportState(prev, snapshot(), ctrl);
    importRefreshRef.current = refresh;
    const unsub = useImportStore.subscribe(refresh);

    return () => {
      unsub();
      importRefreshRef.current = null;
      ctrl.destroy();
      importCtrlRef.current = null;
    };
  }, [styleReady]);

  // AK-368c: смена подсветки строки → зеркалим в ref + реаплаим оверлей (без
  // пересоздания контроллера).
  useEffect(() => {
    importFocusIndexRef.current = importFocusIndex;
    importRefreshRef.current?.();
  }, [importFocusIndex]);

  // AK-368c: лениво грузим хозяйства + имена полей при первом входе в импорт
  // (для дропдауна назначения и автоген «Поле N»). Один раз за сессию панели.
  const importFetchedRef = useRef(false);
  useEffect(() => {
    if (importPhase === 'idle' || importFetchedRef.current) return;
    importFetchedRef.current = true;
    void (async () => {
      const [fr, names] = await Promise.all([
        api.get<{ id: string; name: string }[]>('/api/v1/farms').catch(() => []),
        api
          .get<{ items?: { name: string }[] }>('/api/v1/fields?limit=1000')
          .catch(() => ({ items: [] })),
      ]);
      setImportFarms(fr.map((f) => ({ id: f.id, name: f.name })));
      setImportExistingNames((names.items ?? []).map((r) => r.name).filter(Boolean));
    })();
  }, [importPhase]);

  // AK-369: грузим отделения/бригады выбранного хозяйства (для дропдауна
  // назначения в форме сохранения). Меняется хозяйство → перегружаем; сброс
  // хозяйства → пустой список. listBrigades глотает ошибки → [] (форма прячет
  // поле, привязка идёт к хозяйству).
  useEffect(() => {
    if (!importFarmId) {
      setImportBrigades([]);
      return;
    }
    let cancelled = false;
    void listBrigades(importFarmId).then((bs) => {
      if (!cancelled) setImportBrigades(bs);
    });
    return () => {
      cancelled = true;
    };
  }, [importFarmId]);

  // AK-368c: как только пришло превью — best-effort пометка пересечений с уже
  // существующими полями (клиентски, turf-free). Кандидаты = видимые поля во
  // вьюпорте (field-click-target), их геометрия из кэша/загрузки. Дорого/
  // ненадёжно на больших пачках → ловим ошибки, не блокируем превью.
  useEffect(() => {
    if (importPhase !== 'preview') return;
    const map = mapRef.current;
    if (!map) return;
    let cancelled = false;
    void (async () => {
      try {
        const feats = map.queryRenderedFeatures(undefined, {
          layers: map.getLayer('field-click-target') ? ['field-click-target'] : [],
        });
        const ids = Array.from(
          new Set(
            feats
              .map((f) => (f.properties as { id?: string } | undefined)?.id)
              .filter((id): id is string => !!id),
          ),
        ).slice(0, 300); // потолок кандидатов — тяжёлые вьюпорты не роняем
        const candidates: ExistingFieldRings[] = [];
        for (const id of ids) {
          const geom = getCachedFieldGeometry(id) ?? (await loadFieldGeometry(id).catch(() => null));
          if (!geom) continue;
          candidates.push({
            id: geom.fieldId,
            name: geom.name,
            rings: geom.outerRings as LngLatPt[][],
            bbox: geom.bbox,
          });
        }
        if (cancelled || !candidates.length) return;
        const rows = useImportStore.getState().rows;
        const byIndex: Record<number, string> = {};
        for (const r of rows) {
          if (!r.valid || !r.geometry) continue;
          const hit = firstIntersectingField(r.geometry, candidates);
          if (hit) byIndex[r.index] = hit.name;
        }
        if (!cancelled && Object.keys(byIndex).length) {
          useImportStore.getState().setIntersections(byIndex);
        }
      } catch {
        // best-effort — молча пропускаем.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Зависим только от входа в превью (rows-identity меняется при правке, но
    // повторный прогон пересечений на каждый rename избыточен).
  }, [importPhase]);

  // AK-368c «Уточнить контур» одной строки: beginEdit(index) → грузим её кольцо
  // в редактор terra-draw (тот же путь, что «+ Поле» → «Редактировать»). Возврат
  // из редактора (prevEditorActive-эффект ниже) кладёт уточнённый контур в строку.
  const importEditingIndex = useImportStore((s) => s.editingIndex);
  useEffect(() => {
    if (importEditingIndex === null) return;
    const map = mapRef.current;
    if (!map) return;
    const row = useImportStore.getState().rows.find((r) => r.index === importEditingIndex);
    const ring = importRingOf(row?.geometry ?? null);
    if (!ring) {
      // Нет пригодного внешнего кольца — отменяем правку.
      useImportStore.getState().cancelEdit();
      return;
    }
    importEditIndexRef.current = importEditingIndex;
    editedRingRef.current = ring;
    useEditorStore.getState().activate();
    const t = window.setTimeout(() => {
      editorCtrlRef.current?.loadRing(ring);
    }, 0);
    return () => window.clearTimeout(t);
  }, [importEditingIndex]);


  // AK-334 «Редактировать»: editSignal-инкремент → передать черновик-контур в
  // редактор контура (AK-321/322). Активируем редактор и грузим кольцо. Editor
  // остаётся изолированным — мы дёргаем его публичный activate() + loadRing().
  const editSignal = useAddFieldStore((s) => s.editSignal);
  useEffect(() => {
    if (editSignal === 0) return;
    const geom = useAddFieldStore.getState().draftGeometry;
    const ring = geom?.coordinates?.[0] as LngLat[] | undefined;
    if (!ring) return;
    // Сид ref'а исходным кольцом: если user откроет редактор и сразу «Готово»
    // без правок, onRingChange мог не успеть выстрелить — вернём исходный контур.
    editedRingRef.current = ring;
    // AK-590: редактор открывает режим «+ Поле» — контур на выходе наш.
    addFieldEditRef.current = true;
    // Включаем редактор (его lifecycle-эффект создаст контроллер, если ещё нет).
    useEditorStore.getState().activate();
    // Дать подписке editorStore смонтировать terra-draw до загрузки кольца.
    const t = window.setTimeout(() => {
      editorCtrlRef.current?.loadRing(ring);
    }, 0);
    return () => window.clearTimeout(t);
  }, [editSignal]);

  // AK-373 «Нарисовать»: manualDrawSignal-инкремент → войти в редактор и
  // запустить ПОТОЧЕЧНОЕ рисование контура (terra-draw polygon-draw-mode).
  // Заменяет прежний seed-квадрат (#411): пользователь ставит точки кликами и
  // замыкает контур сам, как в обычном GIS. По замыканию редактор сам переходит
  // в select-mode для правки; «Готово» вернёт контур в форму (тот же путь, что
  // и после «Редактировать»: return-from-editor эффект). setTimeout(0) —
  // дать подписке editorStore смонтировать terra-draw до setMode(polygon).
  const manualDrawSignal = useAddFieldStore((s) => s.manualDrawSignal);
  useEffect(() => {
    if (manualDrawSignal === 0) return;
    // Рисуем с нуля — стартового кольца нет; «Готово» без единой точки вернёт в
    // форму без геометрии (return-from-editor: ring null → openForm без сохранения).
    editedRingRef.current = null;
    // AK-590: редактор открывает режим «+ Поле» — контур на выходе наш.
    addFieldEditRef.current = true;
    useEditorStore.getState().activate();
    const t = window.setTimeout(() => {
      editorCtrlRef.current?.startPolygonDraw();
    }, 0);
    return () => window.clearTimeout(t);
  }, [manualDrawSignal]);

  // AK-334 возврат из редактора: когда идёт add-field editing и редактор
  // закрывается («Готово»/«×»/Esc-выход), берём уточнённое кольцо (последнее
  // из onRingChange) и переводим форму сохранения. Если кольца нет — назад в draft.
  const prevEditorActiveRef = useRef(false);
  useEffect(() => {
    const wasActive = prevEditorActiveRef.current;
    prevEditorActiveRef.current = editorActive;
    if (!(wasActive && !editorActive)) return;

    // AK-590: РЕШЕНИЕ принимает чистая decideReturnFromEditor (её можно
    // тестировать; инлайн-версия в этом файле была недостижима для тестов и
    // именно поэтому пропустила и AK-577, и AK-590). Здесь — только сбор фактов
    // и исполнение намерения.
    //
    // Главный источник контура — сам контроллер: takeFinalGeometry() снимает
    // геометрию из terra-draw синхронно, в момент закрытия. Рефы наполняются
    // отложенными колбэками (recompute идёт через requestAnimationFrame), из-за
    // чего к этому моменту могли быть пустыми — и «Готово» стирало нарисованное
    // поле, не открыв формы (Safari, v1.1599). Рефы остаются фолбэком:
    // контроллер мог быть пересоздан (styleReady) между рисованием и закрытием.
    const af = useAddFieldStore.getState();
    const action = decideReturnFromEditor({
      owner: editorDiscardRef.current
        ? 'discard'
        : importEditIndexRef.current !== null
          ? 'import'
          : addFieldEditRef.current
            ? 'add-field'
            : null,
      importIndex: importEditIndexRef.current,
      finalGeometry: editorCtrlRef.current?.takeFinalGeometry() ?? null,
      refGeometry: editedGeometryRef.current,
      refRing: editedRingRef.current,
      addFieldActive: af.active,
      addFieldPhase: af.phase,
      tracedExistingContour: editorTracedRef.current,
      canCreateField,
    });

    switch (action.type) {
      case 'import-apply': {
        const ring = action.geometry.coordinates[0];
        useImportStore
          .getState()
          .applyEdit(action.index, action.geometry, polygonAreaSqm(ring) / 10_000);
        break;
      }
      case 'import-cancel':
        useImportStore.getState().cancelEdit();
        break;
      case 'add-field-apply':
        af.applyEdited(action.geometry, geometryAreaHa(action.geometry));
        break;
      case 'add-field-reset':
        // Рисование прервали без готового контура → назад в armed текущего
        // способа (не в форму без геометрии).
        af.reset();
        break;
      case 'draw-save-as-new': {
        // AK-591: нарисованное в безвладельном редакторе («Обвести поле» с
        // инструментами рисования) не выбрасываем — включаем «+ Поле» и
        // открываем форму сохранения с этой геометрией. Ровно тот же конец
        // пути, что и у «+ Поле» → «Нарисовать» → «Готово».
        const store = useAddFieldStore.getState();
        store.activate();
        store.setMethod('draw');
        store.applyEdited(action.geometry, geometryAreaHa(action.geometry));
        break;
      }
      case 'noop':
        break;
    }

    // Владение и оба реф-снимка снимаем ВСЕГДА. Раньше чистился только geometry,
    // и кольцо от прошлого захода доживало до следующего: открыл редактор, вышел
    // не дорисовав — и в форму могла подставиться геометрия предыдущего поля.
    importEditIndexRef.current = null;
    addFieldEditRef.current = false;
    editorTracedRef.current = false;
    editorDiscardRef.current = false;
    editedGeometryRef.current = null;
    editedRingRef.current = null;
  }, [editorActive, canCreateField]);

  // AK-334: если режим «+ Поле» выключили, пока черновик был в редакторе
  // (editing), закрываем и редактор — чистый выход, черновик отбрасывается.
  // Отличаем «editor открыт под add-field» по editedRingRef (сид при editSignal):
  // если add-field только что выключился и editor ещё активен — гасим его.
  const prevAddFieldActiveRef = useRef(false);
  useEffect(() => {
    const was = prevAddFieldActiveRef.current;
    prevAddFieldActiveRef.current = addFieldActive;
    if (was && !addFieldActive && useEditorStore.getState().active) {
      // Сбросим ref, чтобы prevEditorActive-эффект не увёл в форму
      // (add-field уже неактивен → его guard и так не сработает, но чистим).
      editedRingRef.current = null;
      importEditIndexRef.current = null;
      addFieldEditRef.current = false;
      editorTracedRef.current = false;
      // Владение снято НАМЕРЕННО (явная «Отмена»/выключение режима) — выход
      // из редактора ниже обязан быть noop, а не спасением draw-save-as-new.
      editorDiscardRef.current = true;
      useEditorStore.getState().deactivate();
    }
    // AK-368c: выход из режима «+ Поле» отменяет незавершённый импорт (чистый
    // выход — пачка отбрасывается, оверлей снимается applyImportState на idle).
    if (was && !addFieldActive && useImportStore.getState().phase !== 'idle') {
      useImportStore.getState().reset();
    }
  }, [addFieldActive]);

  // AK-334 Esc → чистый выход из режима «+ Поле» (краевой случай wireframe).
  // Не перехватываем Esc в фазе editing (там Esc редактора снимает выделение)
  // и когда фокус в поле ввода формы.
  useEffect(() => {
    if (!addFieldActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const af = useAddFieldStore.getState();
      // В редакторе/рисовании Esc обрабатывает terra-draw (снять выделение /
      // отменить последнюю точку) — не гасим весь режим «+ Поле».
      if (af.phase === 'editing' || af.phase === 'drawing') return;
      const t = e.target;
      if (
        t instanceof HTMLElement &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)
      ) {
        return;
      }
      af.deactivate();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addFieldActive]);

  // AK-334 MVT-инвалидация после сохранения поля: пересоздаём fields-mvt с
  // `_v`-timestamp'ом (MapLibre кэширует тайлы по (source,z,x,y), не по URL —
  // CLAUDE.md «Регрессии MVT-фильтра»), чтобы новое поле появилось без reload.
  const invalidateFieldsMvt = useCallback(() => {
    // AK-587: растровая половина той же инвалидации. Геометрия поменялась →
    // спутниковый слой перечитывает версию скоупа и получает НОВЫЙ URL с `_v`,
    // иначе nginx (ключ по $request_uri, 30 д) продолжит отдавать PNG с
    // до-правочным контуром, пока юзер не сменит хозяйство или не перезагрузит.
    requestScopeGeomVersionRefresh();
    const map = mapRef.current;
    if (!map) return;
    const base = buildMvtUrl(useMapStore.getState().activeScope, activeSeasonId);
    const sep = base.includes('?') ? '&' : '?';
    const url = `${base}${sep}_v=${Date.now()}`;
    recreateMvtSource(map, 'fields-mvt', url, { type: 'vector', minzoom: 7, maxzoom: 18 });
  }, [activeSeasonId]);

  /**
   * AK-772: поле изменилось действием из попапа — карта обязана показать это
   * БЕЗ перезагрузки страницы.
   *
   *   · `invalidateFieldsMvt` — новый URL тайлов с `_v` (MapLibre кэширует по
   *     (source,z,x,y), не по URL — CLAUDE.md «Регрессии MVT-фильтра»);
   *   · `invalidateMapInit` — chip-кэш навигатора (счётчики полей в
   *     хозяйствах), тот же вызов, что делает архивация в карточке и в списке;
   *   · попап закрываем: он утверждает про поле, которого уже нет, либо
   *     печатает старое имя из MVT-свойств, снятых до правки.
   *
   * `afterMvRefresh` — про ЛАГ ВИТРИНЫ. Существование поля тайл читает из
   * живой `fields` (`WHERE archived_at IS NULL`), поэтому архивация видна
   * сразу. А вот ПОДПИСЬ поля тайл берёт из материализованной витрины
   * `field_visual_attrs`, которую рефрешит слушатель NOTIFY внутри бэкенда
   * (AK-586, `services/map_mv_scheduler.py`) с дебаунсом ~2 с. Одна только
   * немедленная инвалидация после переименования успела бы утащить тайл со
   * СТАРОЙ подписью — и человек прочитал бы это как «переименование не
   * сработало». Поэтому для имени просим тайлы второй раз, когда витрина
   * заведомо обновилась.
   */
  const refreshAfterFieldChange = useCallback(
    (opts?: { afterMvRefresh?: boolean }) => {
      invalidateMapInit();
      invalidateFieldsMvt();
      fieldPopupRef.current?.remove();
      if (opts?.afterMvRefresh) {
        // Отложенный слот один: следующая правка отодвигает окно, а не заводит
        // ещё один перезапрос тайлов рядом.
        if (mvLagTimerRef.current !== null) {
          window.clearTimeout(mvLagTimerRef.current);
        }
        mvLagTimerRef.current = window.setTimeout(() => {
          mvLagTimerRef.current = null;
          invalidateFieldsMvt();
        }, MAP_MV_LAG_MS);
      }
    },
    [invalidateFieldsMvt],
  );

  const handleFieldArchive = useCallback(
    async (reason: string) => {
      const target = fieldAction;
      if (!target) return;
      setArchivePending(true);
      try {
        const outcome = await archiveSingleField(
          target.fieldId,
          target.fieldName,
          reason,
        );
        toast[outcome.kind](outcome.message);
        if (outcome.archived) {
          refreshAfterFieldChange();
          setFieldAction(null);
        }
        // Отказ — диалог оставляем открытым: причина набрана, человек видит
        // тост «почему» и может повторить, не набирая её заново.
      } finally {
        setArchivePending(false);
      }
    },
    [fieldAction, refreshAfterFieldChange],
  );

  // AK-368c: сохранить пачку импорта. Собираем valid+выбранные строки, имена по
  // namesSource, POST /import/save, прогресс, тост, MVT-инвалидация. Определён
  // ПОСЛЕ invalidateFieldsMvt (зависимость useCallback — избегаем TDZ).
  const handleImportSave = useCallback(async () => {
    const s = useImportStore.getState();
    if (!s.farmId) return;
    const farmName = importFarms.find((f) => f.id === s.farmId)?.name ?? 'хозяйство';
    const chosen = s.rows.filter((r) => r.valid && r.selected && r.geometry);
    if (!chosen.length) return;

    // Имена: из файла (row.name или автоген если пусто) ИЛИ автоген «Поле N».
    const autoStart = nextFieldName(importExistingNames);
    const mBase = /(\d+)\s*$/.exec(autoStart);
    const startN = mBase ? Number.parseInt(mBase[1], 10) : 1;
    const prefix = autoStart.replace(/\d+\s*$/, '').trim() || 'Поле';

    // AK-369: имена по режиму nameMode — 'auto' всегда автоген «Поле N»,
    // 'map'/'table' — имя строки (row.name), пустое → тот же автоген.
    const features: SaveFeaturePayload[] = chosen.map((r, i) => {
      const name =
        s.nameMode === 'auto' || !r.name.trim()
          ? `${prefix} ${startN + i}`
          : r.name.trim();
      return {
        geometry: r.geometry!,
        name,
        number: r.number,
        area_ha: r.areaHa ?? undefined,
      };
    });

    s.beginSave();
    // Прогресс визуальный (save — один батч-запрос; анимируем до ответа).
    s.setSaveProgress(0.15);
    try {
      // AK-369: brigade_id опционально (отделение/бригада пачки).
      const res = await saveImport({
        farm_id: s.farmId,
        brigade_id: s.brigadeId,
        features,
      });
      s.setSaveProgress(1);
      const created = res.created ?? features.length;
      // AK-369: batch_id для кнопки «Отменить импорт» в тосте (null если старый бэк).
      s.completeSave(
        `${created} ${pluralField(created)} добавлено в «${farmName}»`,
        res.batch_id,
      );
      invalidateFieldsMvt();
    } catch (err) {
      const msg =
        err instanceof Error && err.message
          ? err.message
          : 'Не удалось сохранить поля. Попробуйте ещё раз.';
      s.failSave(msg);
    }
  }, [importFarms, importExistingNames, invalidateFieldsMvt]);

  // AK-369: инлайн-создание отделения/бригады из формы сохранения. POST
  // /brigades → id; добавляем в локальный список (дропдаун сразу видит новое) и
  // возвращаем id вызывающему (BrigadeField сам выберет). Ошибка → тост + null
  // (выбор не переключается). Не async в самом теле возврата — панель ждёт
  // Promise<string | null>.
  const handleCreateBrigade = useCallback(
    async (name: string): Promise<string | null> => {
      const farmId = useImportStore.getState().farmId;
      if (!farmId) return null;
      try {
        const b = await createBrigade(farmId, name);
        setImportBrigades((prev) =>
          prev.some((x) => x.id === b.id) ? prev : [...prev, b],
        );
        return b.id;
      } catch (err) {
        const msg =
          err instanceof Error && err.message
            ? err.message
            : 'Не удалось создать отделение.';
        useImportStore.getState().failSave(msg);
        return null;
      }
    },
    [],
  );

  // AK-369: откат пачки импорта из тоста «Отменить импорт». Архивирует поля
  // пачки (обратимо, как AK-340), гасит тост, инвалидирует MVT (поля исчезают
  // с карты сразу). Идемпотентно — повторный клик по уже откатанной = 0 archived.
  const handleUndoImport = useCallback(
    (batchId: string) => {
      void (async () => {
        const s = useImportStore.getState();
        try {
          await rollbackImportBatch(batchId);
          s.clearSuccessToast();
          invalidateFieldsMvt();
        } catch {
          s.failSave('Не удалось отменить импорт. Попробуйте ещё раз.');
        }
      })();
    },
    [invalidateFieldsMvt],
  );

  // Bug #2 (Sprint 2026-19, frontend part): fields-mvt source URL swap on
  // scope change — добавляет farm_id/cluster_id query-param. Backend
  // filter respect'ает (parallel work — bugs 2+4 backend).
  //
  // AK-118 hotfix (2026-05-13, regression of AK-106 lock-in #4):
  //   `setSourceTiles` (a.k.a. VectorTileSource.setTiles) invalidates only
  //   PENDING fetches; MapLibre's tile cache is keyed by `(sourceId, z, x, y)`
  //   — not by URL — so already-rendered tiles from the previous scope keep
  //   painting their features alongside fresh ones. Founder symptom: select
  //   farm «Руно» → contours from farm «Русь» still visible on the map.
  //
  //   Fix: full source recreation (removeLayer × N → removeSource → addSource
  //   → addLayer × N) evicts the cache entirely. Pattern mirrors legacy
  //   `setupMapLayers.ts:798-830::recreateFieldsSource`; extracted to a shared
  //   helper (`lib/map/recreateMvtSource.ts`) so both V2 and legacy can share
  //   the eviction guarantee.
  //
  // AK-485: тот же принцип — теперь и на смену сезона. `activeSeasonId` в
  // deps → смена сезона тоже пересоздаёт source (не просто setTiles), иначе
  // залипли бы тайлы предыдущего сезона (ровно тот баг, что чинит хотфикс
  // на бэкенде — здесь его фронт-эквивалент по механизму кэша).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    recreateMvtSource(map, 'fields-mvt', buildMvtUrl(activeScope, activeSeasonId), {
      type: 'vector',
      minzoom: 7,
      maxzoom: 18,
    });
  }, [styleReady, activeScope, activeSeasonId]);

  // ============================================================
  // AK-149 — Map chips (company / cluster / farm HTML markers).
  //
  // Existing port: `frontend-vite/src/components/map/useFarmChips.ts`
  // (used by legacy MapCanvas). This block wires the same hook into
  // V2 MapPage so MapPageV2 shows the same chip overlay.
  //
  // Inputs derived from V2 store + map zoom:
  //   - activeScope → farmFilter / clusterFilter
  //   - map zoom (moveend) → userLevel (1..12 via zoomLevels.ts)
  //   - companyRevealed → local state, reset on scope change
  //
  // persistScope is the chip-click side-effect:
  //   1. SET_ACTIVE_SCOPE → reducer updates store → fields-mvt source
  //      recreate (existing useEffect above) → fitBounds via /scope/extents
  //   2. dispatchScopeChanged → wakes Dashboard listeners
  //   3. URL search params (`?farm=<uuid>` / `?cluster=<uuid>`) →
  //      AC 11 (shareable URL), via window.history.replaceState
  //
  // Mirror: src/components/map/MapCanvas.tsx:211 (legacy V1 wiring).
  // ============================================================

  const [companyRevealed, setCompanyRevealed] = useState(false);
  const [userLevel, setUserLevel] = useState<number>(12);
  const [mapInit, setMapInit] = useState<MapInitData | null>(null);

  // AC 12 (graceful empty): fetch lazily; tolerate failure (chip overlay
  // simply stays hidden — backend /map-init never blocks map render).
  useEffect(() => {
    if (!hydrated) return;
    let cancelled = false;
    getMapInit()
      .then((data) => {
        if (!cancelled) setMapInit(data);
      })
      .catch((err) => {
        if (import.meta.env.DEV) console.warn('[mapV2] getMapInit failed', err);
        // Sentry is wired up at the boundary level — no captureException
        // here чтобы не спамить при offline / 401 in dev.
      });
    return () => {
      cancelled = true;
    };
  }, [hydrated]);

  // Track user-level (1..12) from map zoom. moveend covers both pan-driven
  // zoom changes and programmatic fitBounds. Reset companyRevealed when
  // scope changes (founder UAT: «после выбора farm и возврата на company
  // должны снова видеть company-pill, не cluster-fan»).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    // Seed once — map is created at zoom=7 (level 8); without seed first
    // render shows default level=12 chip layout briefly.
    setUserLevel(userLevelFromMap(map));
    const onMove = () => setUserLevel(userLevelFromMap(map));
    map.on('moveend', onMove);
    return () => {
      map.off('moveend', onMove);
    };
  }, [styleReady]);

  useEffect(() => {
    // Reset reveal when user navigates back to company scope so the
    // company-pill reappears at level 12.
    if (activeScope.type === 'company') setCompanyRevealed(false);
  }, [activeScope.type, activeScope.id]);

  // AC 11 — URL persistence (write half). The read half lives in the hydrate
  // effect above: AK-296 made `?farm=`/`?cluster=` the PRIORITY scope input on
  // mount via `scopeFromUrl()` (a shared link must restore the link's scope,
  // overriding the server pointer). This callback is the *output* channel —
  // chip-driven scope changes rewrite the URL so it stays shareable.
  const persistScopeUrl = useCallback((type: 'farm' | 'cluster', id: string) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('farm');
    url.searchParams.delete('cluster');
    url.searchParams.set(type, id);
    window.history.replaceState({}, '', url.toString());
  }, []);

  const persistScope = useCallback(
    (type: 'farm' | 'cluster', id: string) => {
      // 1. Update store → triggers MVT recreate + fitBounds via scope-changed.
      dispatch({ type: 'SET_ACTIVE_SCOPE', scope: { type, id } });
      // 2. Notify other widgets (Dashboard, summary, etc.).
      dispatchScopeChanged({ scope_type: type, scope_id: id });
      // 3. Shareable URL.
      persistScopeUrl(type, id);
    },
    [dispatch, persistScopeUrl],
  );

  // Sub-marker click (DBSCAN-группа полей внутри farm) — долёт до bbox
  // группы. Hook expects `(s) => void`; we pull mapRef.current at call time.
  const onSubMarkerClick = useCallback(
    (s: { bbox: [number, number, number, number] }) => {
      const map = mapRef.current;
      if (!map) return;
      const [x1, y1, x2, y2] = s.bbox;
      map.fitBounds(
        [
          [x1, y1],
          [x2, y2],
        ],
        {
          padding: 60,
          maxZoom: mlForUserLevel(map, 5),
          duration: 700,
          essential: true,
        },
      );
    },
    [],
  );

  const farmFilter = activeScope.type === 'farm' ? activeScope.id : null;
  const clusterFilter = activeScope.type === 'cluster' ? activeScope.id : null;

  useFarmChips({
    mapRef,
    markers: mapInit?.markers ?? [],
    clusterMarkers: mapInit?.cluster_markers ?? [],
    subMarkers: mapInit?.sub_markers ?? [],
    farmFilter,
    clusterFilter,
    userLevel,
    companies: mapInit?.companies ?? [],
    companyRevealed,
    setCompanyRevealed,
    persistScope,
    onSubMarkerClick,
  });

  return (
    <AppShell>
      {/*
        Bug #6 (AK Sprint 2026-19): map container must fill AppShell's
        <main> content area, NOT viewport — иначе sidebar (72px) и topbar
        перекрываются absolute fullscreen overlay'ем.
        Pattern скопирован из legacy.tsx (proven): negate <main>'s padding
        (p-3 на mobile / p-6 на desktop) и расширяем на 100%+padding*2.
        Все overlay-компоненты (RightRail, SatelliteStrip) с
        position:absolute внутри этого relative wrapper —
        позиционируются относительно map canvas, а не viewport.
      */}
      <div className="relative -m-3 md:-m-6 h-[calc(100%+24px)] md:h-[calc(100%+48px)] w-[calc(100%+24px)] md:w-[calc(100%+48px)]">
        <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} data-testid="map-canvas-v2" />
        {/* Follow-up AK-383 (founder UAT 2026-07-07): заметный индикатор
            загрузки снимка ПО ЦЕНТРУ карты. Прежний спиннер на полосе дат был
            «спрятан» — founder попросил в центр. Карту не блокирует
            (pointer-events только на плашке). Сигнал — тот же
            useRasterTilesLoading, что кормит SatelliteStrip. */}
        <RasterLoadingOverlay
          loading={satelliteTilesLoading}
          data-testid="map-raster-loading-overlay"
        />
        {/* «Посев с карты»: блок посева живёт внутри HTML-попапа поля (портал в
            его слот), поэтому размонтируется вместе с закрытием попапа. */}
        {sowMount &&
          createPortal(
            <FieldSowBlock
              key={sowMount.fieldId}
              fieldId={sowMount.fieldId}
              fieldName={sowMount.fieldName}
              fallbackCrop={sowMount.crop}
              fallbackPhase={sowMount.phase}
              onSown={setSowToast}
            />,
            sowMount.node,
          )}
        {/* AK-772 «выверка полей с карты»: действия над полем — в самом
            попапе. Портал, как и блок посева; диалоги — ниже, вне попапа. */}
        {actionsMount &&
          createPortal(
            <FieldActionsBlock
              key={actionsMount.fieldId}
              fieldId={actionsMount.fieldId}
              fieldName={actionsMount.fieldName}
              onRename={() =>
                setFieldAction({
                  kind: 'rename',
                  fieldId: actionsMount.fieldId,
                  fieldName: actionsMount.fieldName,
                  hasCrop: actionsMount.hasCrop,
                })
              }
              onArchive={() =>
                setFieldAction({
                  kind: 'archive',
                  fieldId: actionsMount.fieldId,
                  fieldName: actionsMount.fieldName,
                  hasCrop: actionsMount.hasCrop,
                })
              }
            />,
            actionsMount.node,
          )}
        {/* Переименование — ТОТ ЖЕ диалог, что в карточке поля: второго
            способа переименовать поле в системе не заводим. PATCH partial
            (#391) — базового payload'а не нужно, довольно имени. */}
        {fieldAction?.kind === 'rename' && (
          <RenameFieldDialog
            open
            onOpenChange={(o) => {
              if (!o) setFieldAction(null);
            }}
            fieldId={fieldAction.fieldId}
            currentName={fieldAction.fieldName}
            fieldPayloadBase={{}}
            onRenamed={() => {
              // Имя живёт в витрине подписей — просим тайлы ещё раз за окном
              // её рефреша (см. MAP_MV_LAG_MS), иначе на карте останется
              // старая подпись и правка прочитается как несработавшая.
              refreshAfterFieldChange({ afterMvRefresh: true });
              setFieldAction(null);
            }}
          />
        )}
        {/* Архивация — ТОТ ЖЕ диалог с обязательной причиной, что у чистки
            списка полей (AK-750): через месяц на вопрос «почему этого поля
            нет» отвечает только она. */}
        {fieldAction?.kind === 'archive' && (
          <FieldsArchiveDialog
            open
            subject={fieldAction.fieldName}
            stats={{ count: 1, areaHa: 0, occupied: fieldAction.hasCrop ? 1 : 0 }}
            pending={archivePending}
            onCancel={() => setFieldAction(null)}
            onConfirm={handleFieldArchive}
          />
        )}
        {/* Подтверждение посева — pill top-center, как successToast у
            AddFieldPanel / ImportFilePanel. Живёт вне попапа: попап может
            закрыться сразу после действия, а подтверждение founder должен
            увидеть. */}
        {sowToast && (
          <div
            data-testid="map-sow-success-toast"
            role="status"
            className="absolute top-4 left-1/2 z-40 max-w-md -translate-x-1/2 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground shadow-lg"
          >
            {sowToast}
          </div>
        )}
        {/* Sprint 2026-25 T-B2: «Аккаунт активирован» toast после
            consultant-creates-proto magic-link accept. Видим 6 секунд,
            потом auto-dismiss. Стиль — overlay top-center с green accent
            (consistent c successful-action toasts в design system). */}
        {promotedToast && (
          <div
            data-testid="promoted-toast"
            className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-lg shadow-lg px-4 py-3 max-w-md flex items-start gap-3"
          >
            {/* Иконка вместо эмодзи ✅ — цветом управляем мы, а не системный
                эмодзи-шрифт (design review 18.08, зона 1 · А9). */}
            <CircleCheck size={24} aria-hidden className="shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm">Аккаунт активирован</div>
              <div className="text-xs text-emerald-800 mt-1">
                Добро пожаловать в ИИ-Колхоз. Вы стали владельцем своего хозяйства.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPromotedToast(false)}
              className="text-emerald-700 hover:text-emerald-900 leading-none"
              aria-label="Закрыть"
            >
              <X size={16} aria-hidden />
            </button>
          </div>
        )}
        {/* AK-388: fail-soft тост при отказе/ошибке геолокации. Top-center,
            neutral/destructive стиль DS. Не блокирует карту, авто-скрытие 6с. */}
        {geoError && (
          <div
            data-testid="geolocate-error-toast"
            role="alert"
            className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex max-w-md items-start gap-3 rounded-lg border border-border bg-background/95 px-4 py-3 text-foreground shadow-lg backdrop-blur-sm"
          >
            <span className="text-lg leading-none" aria-hidden>
              📍
            </span>
            <div className="flex-1 text-sm">{geoError}</div>
            <button
              type="button"
              onClick={() => setGeoError(null)}
              className="leading-none text-muted-foreground hover:text-foreground"
              aria-label="Закрыть"
            >
              ×
            </button>
          </div>
        )}
        {hydrated && (
          <>
            {/* Section 4 layout: Right rail (right) + optional SatelliteStrip
                bottom-center.
                Founder lock-in 2026-05-08: TopBarPill + ScopeDrawer удалены
                с карты — они дублировали единственный scope selector в
                AppShell (тогда пилюля в шапке; с AK-895 — переключатель
                «Хозяйство ▾» вверху сайдбара). Изначальная интерпретация
                Section 4 spec'а ("pill on map") была неверной; founder
                подтвердил: scope-selector живёт только в оболочке.
                "Pills" в map area по founder intent — будущие centroid
                labels (имя farm/cluster/holding в географическом центре
                каждой scope-границы), это отдельная фича вне этой PR.
                AK-103 mount, добавленный 2026-05-08 утром, был регрессией —
                он восстановил mount от Sprint 2026-21 без подтверждения
                founder. Удалили обратно. */}
            {/* T11 (Sprint 2026-21): RightRail migrated to V2 singleton store —
                no `store` prop required. Reads/writes useMapStoreV2 directly,
                same as SatelliteStrip / MapVerticalLegend. */}
            {styleReady && <RightRail map={mapRef.current} />}
            {/* AK-320: панель линейки — монтируется при активном measure-туле. */}
            {styleReady && measureActive && <MeasurePanel />}
            {/* AK-321: панель редактора контура — при активном «обвести поле». */}
            {styleReady && editorActive && <ContourEditorPanel />}
            {/* AK-334/335: панель «+ Поле» (черновик/форма/тосты). Скрыта, пока
                контур передан в редактор (editing) — тогда виден ContourEditorPanel. */}
            {styleReady && addFieldActive && !editorActive && importPhase === 'idle' && (
              <AddFieldPanel onSaved={invalidateFieldsMvt} />
            )}
            {/* AK-584 шаг 3: пришли из мастера и первое поле сохранено —
                вместо гаснущего тоста показываем карточку «Готово» с честным
                ожиданием по снимкам.

                Гейт по фазе обязателен: карточка занимает тот же нижний слот
                (bottom-4, z-40), что и лист добавления поля и пилюля «Ищу
                границы…». Человек после сохранения остаётся в режиме и часто
                сразу тапает следующее поле, не нажимая «Добавить ещё» — без
                гейта форма рисовалась бы поверх карточки. Показываем только в
                покое: фаза armed и импорт не идёт. */}
            {styleReady &&
              shouldShowFirstFieldDone({
                onboardingRun,
                savedCount: addFieldSavedCount,
                dismissed: doneCardDismissed,
                phase: addFieldPhase,
                importPhase,
              }) && (
                <FirstFieldDoneCard
                  savedCount={addFieldSavedCount}
                  onAddMore={() => setDoneCardDismissed(true)}
                  onFinish={() => {
                    setDoneCardDismissed(true);
                    setOnboardingRun(false);
                    useAddFieldStore.getState().deactivate();
                  }}
                />
              )}
            {/* AK-368c: панель импорта из файла (лоадер/превью/сохранение/ошибка).
                Живёт пока importPhase !== 'idle'. В фазе «Уточнить контур» одной
                строки редактор terra-draw активен — панель списка остаётся
                видимой (пачка приглушена ImportController'ом), кроме самого
                момента редактирования, когда виден ContourEditorPanel. */}
            {styleReady && importPhase !== 'idle' && !editorActive && (
              <ImportFilePanel
                farms={importFarms}
                brigades={importBrigades}
                existingNames={importExistingNames}
                onRowFocus={setImportFocusIndex}
                onEditContour={(index) => useImportStore.getState().beginEdit(index)}
                onSave={() => void handleImportSave()}
                onCreateBrigade={handleCreateBrigade}
                onUndoImport={handleUndoImport}
              />
            )}
            {satelliteStripOpen && <SatelliteStrip tilesLoading={satelliteTilesLoading} />}
            {/* T10 (Sprint 2026-21): MapVerticalLegend reads from
                useMapStoreV2 directly (founder lock-in #5/#6 — draggable +
                culture palette). Self-hides when activeRasterMode='none' AND
                cropFillOn=false; otherwise renders the appropriate palette. */}
            <MapVerticalLegend />
          </>
        )}
      </div>
    </AppShell>
  );
}
